import { useEffect, useRef, useState } from 'react'

import { GetScreenState as BLGetScreenState } from './../../utilities/BL/Admin/Overlays/Noisemeter'

import { motion } from 'framer-motion'
import Confetti from 'react-confetti'


const Noisemeter = ({ data, isActiveWomensMode }) => { 
    
    // --- ESTADOS ---
    const [gameState, setGameState] = useState('WAITING'); 
    const [zones, setZones] = useState([]);
    const [winner, setWinner] = useState(null);

    const pollingRef = useRef(null);
    const { innerWidth: width, innerHeight: height } = window;

    // --------------------------------------------------------------------------------
    // LÓGICA DE DATOS
    // --------------------------------------------------------------------------------
    useEffect(() => {
        
        const updateScreen = (incoming) => {
            if (!incoming) return;

            // 1. NORMALIZACIÓN DE DATOS
            const rawStatus = incoming.status !== undefined ? incoming.status : incoming.Status;
            const rawWinner = incoming.winnerZone || incoming.WinnerZone;
            
            // Buscamos Zones (con datos) Y activeZonesList (solo nombres)
            const rawZones = incoming.zones || incoming.Zones;
            const rawActiveList = incoming.activeZonesList || incoming.ActiveZonesList;
            
            const hasDates = incoming.startAt || incoming.StartAt;

            // 2. ACTUALIZACIÓN DE BARRAS (Lógica Mejorada)
            
            // PRIORIDAD 1: Si vienen zonas con datos (del Polling o Finished), úsalas.
            if (rawZones && Array.isArray(rawZones) && rawZones.length > 0) {
                const cleanZones = rawZones.map(z => ({
                    zone: z.zone || z.Zone,
                    progressPercent: z.progressPercent !== undefined ? z.progressPercent : z.ProgressPercent
                }));
                setZones(cleanZones);
            } 
            // PRIORIDAD 2: Si NO hay datos, pero sí hay lista de nombres (del Prepare), inicializa en 0.
            else if (rawActiveList && Array.isArray(rawActiveList) && rawActiveList.length > 0) {
                // Solo inicializamos si no tenemos zonas ya cargadas, para evitar parpadeos
                // O si queremos forzar el reinicio visual.
                const initialZones = rawActiveList.map(name => ({
                    zone: name,
                    progressPercent: 0
                }));
                setZones(initialZones);
            }

            // 3. DETERMINAR ESTADO

            // --- CASO A: YA HAY GANADOR ---
            if (rawWinner && rawWinner !== 'none' && rawWinner !== 'tie' && rawWinner !== '') {
                setWinner(rawWinner);
                setGameState('FINISHED');
                return; 
            }
            if (rawWinner === 'tie') {
                setWinner('tie');
                setGameState('FINISHED');
                return;
            }
            if (rawStatus === 2 || rawStatus === 'Finished') {
                setGameState('FINISHED');
                return;
            }

            // --- CASO B: JUEGO CORRIENDO ---
            // Criterio 1: Status explícito
            // Criterio 2: Prepare implícito (sin status pero con fechas)
            const isRunningExplicit = rawStatus === 1 || rawStatus === 'Running';
            const isImplicitStart = rawStatus === undefined && hasDates; 

            if (isRunningExplicit || isImplicitStart) {
                setGameState('RUNNING');
                setWinner(null); 
                return;
            }

            // --- CASO C: ESPERA ---
            setGameState('WAITING');
            setWinner(null);
        };

        // A. Procesar datos por props (WebSocket / Padre)
        if (data) updateScreen(data);

        // B. Polling (Consultar API)
        const fetchState = async () => {
            if (typeof BLGetScreenState !== 'undefined') {
                try {
                    const res = await BLGetScreenState();
                    if (res?.success && res?.data) {
                        updateScreen(res.data);
                    }
                } catch (e) {
                    console.error("Error fetching screen state", e);
                }
            }
        };

        if (!pollingRef.current) {
            pollingRef.current = setInterval(fetchState, 500);
        }

        return () => {
            if (pollingRef.current) clearInterval(pollingRef.current);
        };

    }, [data]); 

    // --------------------------------------------------------------------------------
    // RENDERIZADO VISUAL
    // --------------------------------------------------------------------------------
    const getZoneStyle = (zoneName) => {
        const n = (zoneName || '').toLowerCase();
        if (n.includes('azul')) return { colorClass: 'blue', hex: '#004d98' };
        if (n.includes('oro')) return { colorClass: 'gold', hex: '#ffd700' };
        if (n.includes('canels')) return { colorClass: 'blue', hex: '#0055a5' };
        if (n.includes('tecate')) return { colorClass: 'red', hex: '#cb0909' };
        if (n.includes('mobil')) return { colorClass: 'red', hex: '#ee1c25' };
        if (n.includes('red cola')) return { colorClass: 'red', hex: '#da291c' };
        if (n.includes('moctezuma')) return { colorClass: 'green', hex: '#005826' };
        if (n.includes('nissan')) return { colorClass: 'gray', hex: '#666666' };
        return { colorClass: 'red', hex: '#dc3545' };
    };

    const renderVersusMode = () => {
        if (zones.length < 2) return null;
        const zoneA = zones[0];
        const zoneB = zones[1];
        const styleA = getZoneStyle(zoneA.zone);
        const styleB = getZoneStyle(zoneB.zone);

        return (
            <div className="noisemeter-screen__versus">
                <div className="noisemeter-screen__bar-container noisemeter-screen__bar-container--left">
                    <div className="noisemeter-screen__bar-track">
                        <div className={`noisemeter-screen__bar-fill noisemeter-screen__bar-fill--${styleA.colorClass}`} style={{ height: `${zoneA.progressPercent}%`, background: styleA.hex }}>
                            <span className="noisemeter-screen__score">{Math.floor(zoneA.progressPercent)}%</span>
                        </div>
                    </div>
                </div>
                <div className="noisemeter-screen__vs-badge">VS</div>
                <div className="noisemeter-screen__bar-container noisemeter-screen__bar-container--right">
                    <div className="noisemeter-screen__bar-track">
                        <div className={`noisemeter-screen__bar-fill noisemeter-screen__bar-fill--${styleB.colorClass}`} style={{ height: `${zoneB.progressPercent}%`, background: styleB.hex }}>
                            <span className="noisemeter-screen__score">{Math.floor(zoneB.progressPercent)}%</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderMultiMode = () => {
        return (
            <div className="noisemeter-screen__multi">
                {zones.map((z) => {
                    const style = getZoneStyle(z.zone);
                    const labelFontSize = zones.length > 5 ? '1.2rem' : '1.8rem';
                    const labelText = z.zone.replace(/Zona\s/i, ''); 
                    return (
                        <div key={z.zone} className="noisemeter-screen__multi-column">
                            <div className="noisemeter-screen__multi-track">
                                <div className="noisemeter-screen__multi-fill" style={{ height: `${z.progressPercent}%`, background: style.hex, boxShadow: `0 0 20px ${style.hex}60` }}></div>
                            </div>
                            <p className="noisemeter-screen__label" style={{ fontSize: labelFontSize }}>{labelText}</p>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="noisemeter-screen">
            <div className="noisemeter-screen__bg">
                {isActiveWomensMode ? (
                    <img src='/img/selfiefan/Fondo-SelfieFan-Femenil.png' className='noisemeter-screen__bg-img' alt="Fondo Femenil" />
                ) : (
                    <img src='/img/page/backgrounds/background.png' className='noisemeter-screen__bg-img' alt="Fondo Varonil" />
                )}
            </div>

            <div className='Noisemeter__Screen__Box'>
                <div className="noisemeter-screen__header">
                    <img src="/img/page/adsl-five.png" className="Noisemeter__Screen__Logo" alt="Logo" />
                </div>

                <div className="noisemeter-screen__content">
                    {gameState === 'WAITING' && (
                        <p className="Noisemeter__Screen__Text--Title m-auto">Preparando dinámica...</p>
                    )}

                    {gameState === 'RUNNING' && (
                        <>
                           {zones.length === 2 ? renderVersusMode() : renderMultiMode()}
                        </>
                    )}

                    {gameState === 'FINISHED' && (
                        <>
                            <div style={{opacity: 0.3, pointerEvents: 'none', filter: 'blur(5px)'}}>
                                {zones.length === 2 ? renderVersusMode() : renderMultiMode()}
                            </div>

                            {winner && (
                                <div className="Results__FullScreenOverlay">
                                    <Confetti width={width} height={height} recycle={false} numberOfPieces={500} />
                                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1.12 }} transition={{ duration: 0.6, type: 'spring' }} style={{textAlign: 'center'}}>
                                        <p><i className="bi bi-trophy-fill" style={{fontSize: '5rem', color: '#ffc107'}}></i></p>
                                        
                                        {winner === 'tie' ? 
                                            <p className="Results__FullScreenOverlay__OneWinner">¡Ha habido un empate!</p> :
                                            <>
                                                <p className="Results__FullScreenOverlay__Title" style={{fontSize: '3rem'}}>El ganador es...</p>
                                                <p className="Results__FullScreenOverlay__OneWinner">{winner.toUpperCase() === 'CANELS' ? 'LUCHO' : winner.toUpperCase() === 'TECATE' ? 'TUNITA' : winner.toUpperCase() === 'AZUL' ? 'LUCHO' : 'TUNITA'}</p>
                                            </>
                                        }
                                    </motion.div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
            <footer className='SelfieFan__Screen__QR__Footer FooterCredits'>Hecho con ♥ por Interaktia | 2026</footer>
        </div>
    );
};

export { Noisemeter };






























// const Noisemeter = ({ data, isActiveWomensMode }) => { 
    
//     // --- ESTADOS PRINCIPALES ---
//     const [gameState, setGameState] = useState('WAITING'); 
//     const [zones, setZones] = useState([]);
//     const [winner, setWinner] = useState(null);

//     // --- ESTADOS VISUALES ---
//     const [countdownVal, setCountdownVal] = useState(3);
//     const [timeLeft, setTimeLeft] = useState(15); 

//     // --- REFS DE CONTROL ---
//     const hasStartedRef = useRef(false); 
//     const currentMatchIdRef = useRef(null); // Para detectar cambio de partida

//     // Refs de intervalos
//     const pollingRef = useRef(null);
//     const timerIntervalRef = useRef(null);

//     const { innerWidth: width, innerHeight: height } = window;

//     // --------------------------------------------------------------------------------
//     // 1. LÓGICA DE ESCUCHA Y RESET
//     // --------------------------------------------------------------------------------
//     useEffect(() => {
//         const processIncomingData = (incoming) => {
//             if (!incoming) return;

//             // Siempre actualizar zonas (para que se muevan las barras)
//             if (incoming.zones) setZones(incoming.zones);

//             const serverStatus = incoming.status; // 0=Waiting, 1=Running, 2=Finished
//             const incomingMatchId = incoming.matchId;

//             // ---------------------------------------------------------------------
//             // A. DETECCIÓN DE REINICIO (RESET)
//             // ---------------------------------------------------------------------
//             // Si el server dice "Waiting" (0), o si el ID del match cambió respecto al anterior,
//             // forzamos un reseteo total de la pantalla local.
            
//             const isWaiting = serverStatus === 0 || serverStatus === 'Waiting';
//             const isNewMatch = currentMatchIdRef.current && currentMatchIdRef.current !== incomingMatchId;

//             if (isWaiting || isNewMatch) {
//                 // Si estábamos corriendo o finalizados, y de pronto es Waiting o Nuevo Match:
//                 if (gameState !== 'WAITING' || isNewMatch) {
//                     // 1. Limpiamos intervalos locales
//                     if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
                    
//                     // 2. Reseteamos estados visuales
//                     setGameState('WAITING');
//                     setWinner(null);
//                     setCountdownVal(3);
//                     setTimeLeft(15);

//                     // 3. ABRIMOS EL CERROJO para la nueva partida
//                     hasStartedRef.current = false; 
//                     currentMatchIdRef.current = incomingMatchId; // Actualizamos el ID actual
                    
//                     console.log("Pantalla reseteada para nueva partida");
//                 }
                
//                 // Si es solo Waiting, no hacemos nada más, esperamos el Running.
//                 if (isWaiting) return; 
//             }

//             // ---------------------------------------------------------------------
//             // B. DETECCIÓN DE GANADOR
//             // ---------------------------------------------------------------------
//             if (incoming.winnerZone && incoming.winnerZone !== 'none') {
//                 // Solo si no lo hemos marcado ya
//                 if (gameState !== 'FINISHED') {
//                     setWinner(incoming.winnerZone);
//                     setGameState('FINISHED');
//                     if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
//                 }
//                 return;
//             }

//             // ---------------------------------------------------------------------
//             // C. DETECCIÓN DE INICIO (RUNNING)
//             // ---------------------------------------------------------------------
//             const isRunning = serverStatus === 1 || serverStatus === 'Running';

//             if (isRunning) {
//                 // Guardamos el ID si es la primera vez que lo vemos (por si entramos directo a running)
//                 if (!currentMatchIdRef.current) currentMatchIdRef.current = incomingMatchId;

//                 // Si NO hemos arrancado localmente esta partida específica:
//                 if (!hasStartedRef.current) {
//                     console.log("Iniciando secuencia local...");
//                     hasStartedRef.current = true; // <--- CERROJO CERRADO
                    
//                     // Calcular duración
//                     const sTime = new Date(incoming.startAt).getTime();
//                     const eTime = new Date(incoming.endsAt).getTime();
//                     let duration = Math.ceil((eTime - sTime) / 1000);
//                     if (isNaN(duration) || duration <= 0) duration = 15;

//                     startLocalSequence(duration);
//                 }
//             }
//         };

//         // ... resto del código de polling igual ...
//         if (data) processIncomingData(data);

//         const fetchState = async () => {
//             if (typeof BLGetScreenState !== 'undefined') {
//                 const res = await BLGetScreenState();
//                 if (res?.success) processIncomingData(res.data);
//             }
//         };

//         pollingRef.current = setInterval(fetchState, 500);
//         return () => {
//             clearInterval(pollingRef.current);
//             clearInterval(timerIntervalRef.current);
//         };
//     }, [data, gameState]); // gameState nos sirve para comparar cambios


//     // --------------------------------------------------------------------------------
//     // 2. SECUENCIA LOCAL (Sin cambios mayores, solo asegurando limpieza)
//     // --------------------------------------------------------------------------------
    
//     const startLocalSequence = (durationSeconds) => {
//         setGameState('COUNTDOWN'); // Cambia UI a 3, 2, 1
//         setCountdownVal(3);

//         if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);

//         let count = 3;
//         timerIntervalRef.current = setInterval(() => {
//             count--;
//             if (count > 0) {
//                 setCountdownVal(count);
//             } else {
//                 clearInterval(timerIntervalRef.current);
//                 runGameTimer(durationSeconds);
//             }
//         }, 1000);
//     };

//     const runGameTimer = (duration) => {
//         setGameState('RUNNING'); // Cambia UI a Barras
//         setTimeLeft(duration);

//         let localTime = duration;
        
//         timerIntervalRef.current = setInterval(() => {
//             localTime--;
//             setTimeLeft(localTime >= 0 ? localTime : 0);

//             if (localTime <= 0) {
//                 clearInterval(timerIntervalRef.current);
//                 // Aquí esperamos a que el polling traiga al ganador, 
//                 // o podemos poner un timeout de seguridad si el internet falla.
//             }
//         }, 1000);
//     };

//     // ... (El resto de helpers visuales y Render se mantienen igual) ...

//     // HELPERS VISUALES (getZoneStyle, renderVersusMode, renderMultiMode) van aquí...
    
//     // RENDER UI va aquí...
//     // (Asegúrate de copiar el return del código anterior, es idéntico)

//     // ... (Solo incluyo los helpers y render para que el código sea pegable si lo necesitas completo) ...
//     const getZoneStyle = (zoneName) => {
//         const n = (zoneName || '').toLowerCase();
//         if (n.includes('azul')) return { colorClass: 'blue', hex: '#004d98' };
//         if (n.includes('oro')) return { colorClass: 'gold', hex: '#ffd700' };
//         if (n.includes('canels')) return { colorClass: 'blue', hex: '#0055a5' };
//         if (n.includes('tecate')) return { colorClass: 'red', hex: '#cb0909' };
//         if (n.includes('mobil')) return { colorClass: 'red', hex: '#ee1c25' };
//         if (n.includes('red cola')) return { colorClass: 'red', hex: '#da291c' };
//         if (n.includes('moctezuma')) return { colorClass: 'green', hex: '#005826' };
//         if (n.includes('nissan')) return { colorClass: 'gray', hex: '#666666' };
//         return { colorClass: 'red', hex: '#dc3545' };
//     };

//     const renderVersusMode = () => {
//         if (zones.length < 2) return null;
//         const zoneA = zones[0];
//         const zoneB = zones[1];
//         const styleA = getZoneStyle(zoneA.zone);
//         const styleB = getZoneStyle(zoneB.zone);

//         return (
//             <div className="noisemeter-screen__versus">
//                 <div className="noisemeter-screen__bar-container noisemeter-screen__bar-container--left">
//                     <div className="noisemeter-screen__bar-track">
//                         <div className={`noisemeter-screen__bar-fill noisemeter-screen__bar-fill--${styleA.colorClass}`} style={{ height: `${zoneA.progressPercent}%`, background: styleA.hex }}>
//                             <span className="noisemeter-screen__score">{Math.floor(zoneA.progressPercent)}%</span>
//                         </div>
//                     </div>
//                 </div>
//                 <div className="noisemeter-screen__vs-badge">VS</div>
//                 <div className="noisemeter-screen__bar-container noisemeter-screen__bar-container--right">
//                     <div className="noisemeter-screen__bar-track">
//                         <div className={`noisemeter-screen__bar-fill noisemeter-screen__bar-fill--${styleB.colorClass}`} style={{ height: `${zoneB.progressPercent}%`, background: styleB.hex }}>
//                             <span className="noisemeter-screen__score">{Math.floor(zoneB.progressPercent)}%</span>
//                         </div>
//                     </div>
//                 </div>
//             </div>
//         );
//     };

//     const renderMultiMode = () => {
//         return (
//             <div className="noisemeter-screen__multi">
//                 {zones.map((z) => {
//                     const style = getZoneStyle(z.zone);
//                     const labelFontSize = zones.length > 5 ? '1.2rem' : '1.8rem';
//                     const labelText = z.zone.replace(/Zona\s/i, ''); 
//                     return (
//                         <div key={z.zone} className="noisemeter-screen__multi-column">
//                             <div className="noisemeter-screen__multi-track">
//                                 <div className="noisemeter-screen__multi-fill" style={{ height: `${z.progressPercent}%`, background: style.hex, boxShadow: `0 0 20px ${style.hex}60` }}></div>
//                             </div>
//                             <p className="noisemeter-screen__label" style={{ fontSize: labelFontSize }}>{labelText}</p>
//                         </div>
//                     );
//                 })}
//             </div>
//         );
//     };

//     return (
//         <div className="noisemeter-screen">
//             <div className="noisemeter-screen__bg">
//                 {isActiveWomensMode ? (
//                     <img src='/img/selfiefan/Fondo-SelfieFan-Femenil.png' className='noisemeter-screen__bg-img' alt="Fondo Femenil" />
//                 ) : (
//                     <img src='/img/page/backgrounds/background.png' className='noisemeter-screen__bg-img' alt="Fondo Varonil" />
//                 )}
//             </div>

//             <div className='Noisemeter__Screen__Box'>
//                 <div className="noisemeter-screen__header">
//                     <img src="/img/page/adsl-five.png" className="Noisemeter__Screen__Logo" alt="Logo" />
//                     {gameState === 'RUNNING' && timeLeft > 0 && (
//                         <div className="Noisemeter__Screen__Timer">
//                             <i className="bi bi-hourglass-split"></i> {timeLeft} s
//                         </div>
//                     )}
//                 </div>

//                 <div className="noisemeter-screen__content">
//                     {gameState === 'WAITING' && (
//                         <p className="Noisemeter__Screen__Text--Title m-auto">Preparando dinámica...</p>
//                     )}
//                     {gameState === 'COUNTDOWN' && (
//                         <motion.div 
//                             key={countdownVal} 
//                             initial={{ scale: 0.5, opacity: 0 }}
//                             animate={{ scale: 1.5, opacity: 1 }}
//                             exit={{ scale: 2, opacity: 0 }}
//                             transition={{ duration: 0.5 }}
//                             style={{ fontSize: '15rem', fontWeight: '900', color: '#fff', textShadow: '0 0 50px rgba(255,255,255,0.8)', zIndex: 100 }}
//                         >
//                             {countdownVal}
//                         </motion.div>
//                     )}
//                     {gameState === 'RUNNING' && (zones.length === 2 ? renderVersusMode() : renderMultiMode())}
//                     {gameState === 'FINISHED' && (
//                         <>
//                             <div style={{opacity: 0.3, pointerEvents: 'none', filter: 'blur(5px)'}}>
//                                 {zones.length === 2 ? renderVersusMode() : renderMultiMode()}
//                             </div>
//                             {winner && (
//                                 <div className="Results__FullScreenOverlay">
//                                     <Confetti width={width} height={height} recycle={false} numberOfPieces={500} />
//                                     <motion.div initial={{ scale: 0 }} animate={{ scale: 1.12 }} transition={{ duration: 0.6, type: 'spring' }} style={{textAlign: 'center'}}>
//                                         <p><i className="bi bi-trophy-fill" style={{fontSize: '5rem', color: '#ffc107'}}></i></p>
//                                         {winner === 'tie' ? 
//                                             <p className="Results__FullScreenOverlay__OneWinner">¡Ha habido un empate!</p> :
//                                             <>
//                                                 <p className="Results__FullScreenOverlay__Title" style={{fontSize: '3rem'}}>El ganador es...</p>
//                                                 <p className="Results__FullScreenOverlay__OneWinner">{winner.toUpperCase()}</p>
//                                             </>
//                                         }
//                                     </motion.div>
//                                 </div>
//                             )}
//                         </>
//                     )}
//                 </div>
//             </div>
//             <footer className='SelfieFan__Screen__QR__Footer FooterCredits'>Hecho con ♥ por Interaktia | 2026</footer>
//         </div>
//     );
// };

// export { Noisemeter };


























// const Noisemeter = ({ data, isActiveWomensMode }) => { 
    
//     // --- ESTADOS PRINCIPALES ---
//     const [gameState, setGameState] = useState('WAITING'); // WAITING | COUNTDOWN | RUNNING | FINISHED
//     const [zones, setZones] = useState([]);
//     const [winner, setWinner] = useState(null);

//     // --- ESTADOS VISUALES ---
//     const [countdownVal, setCountdownVal] = useState(3);
//     const [timeLeft, setTimeLeft] = useState(15); // Default 15s

//     // --- REFS DE CONTROL (EL CERROJO) ---
//     // Este ref es vital: evita que el polling reinicie la animación
//     const hasStartedRef = useRef(false); 
//     const currentMatchIdRef = useRef(null);

//     // Refs de intervalos
//     const pollingRef = useRef(null);
//     const timerIntervalRef = useRef(null);

//     const { innerWidth: width, innerHeight: height } = window;

//     // --------------------------------------------------------------------------------
//     // 1. ESCUCHAR DATOS (Sincronización de Barras y Ganador)
//     // --------------------------------------------------------------------------------
//     // Este useEffect SOLO actualiza la info visual (barras) y checa si ya hubo ganador.
//     // NO toca los timers directamente.
//     useEffect(() => {
//         const processIncomingData = (incoming) => {
//             if (!incoming) return;

//             // 1. Siempre actualizamos las barras para que se muevan
//             if (incoming.zones) setZones(incoming.zones);

//             // 2. Si hay ganador, forzamos el fin INMEDIATAMENTE
//             if (incoming.winnerZone && incoming.winnerZone !== 'none') {
//                 setWinner(incoming.winnerZone);
//                 setGameState('FINISHED');
//                 clearInterval(timerIntervalRef.current); // Detener cualquier timer local
//                 return;
//             }

//             // 3. DETECTAR SEÑAL DE ARRANQUE
//             const isRunning = incoming.status === 1 || incoming.status === 'Running';
//             const incomingMatchId = incoming.matchId;

//             // Si el server dice "Running", nosotros estamos en "WAITING" y NO hemos arrancado este ID:
//             if (isRunning && gameState === 'WAITING') {
//                 // Checamos si es un match nuevo para resetear el cerrojo
//                 if (currentMatchIdRef.current !== incomingMatchId) {
//                     hasStartedRef.current = false; 
//                     currentMatchIdRef.current = incomingMatchId;
//                 }

//                 // SI NO HEMOS ARRANCADO, ARRANCAMOS AHORA
//                 if (!hasStartedRef.current) {
//                     hasStartedRef.current = true; // <--- CERROJO ACTIVADO
                    
//                     // Calculamos duración una sola vez
//                     const sTime = new Date(incoming.startAt).getTime();
//                     const eTime = new Date(incoming.endsAt).getTime();
//                     let duration = Math.ceil((eTime - sTime) / 1000);
//                     if (isNaN(duration) || duration <= 0) duration = 15;

//                     // Iniciamos la secuencia local
//                     startLocalSequence(duration);
//                 }
//             }
//         };

//         if (data) processIncomingData(data);

//         const fetchState = async () => {
//             // Asumiendo que BLGetScreenState es global
//             if (typeof BLGetScreenState !== 'undefined') {
//                 const res = await BLGetScreenState();
//                 if (res?.success) processIncomingData(res.data);
//             }
//         };

//         pollingRef.current = setInterval(fetchState, 500);
//         return () => clearInterval(pollingRef.current);
//     }, [data, gameState]); // gameState aquí ayuda a saber si ignorar start signals si ya acabamos


//     // --------------------------------------------------------------------------------
//     // 2. SECUENCIA LOCAL (El Motor Independiente)
//     // --------------------------------------------------------------------------------
    
//     const startLocalSequence = (durationSeconds) => {
//         // PASO 1: CUENTA REGRESIVA (3, 2, 1)
//         setGameState('COUNTDOWN');
//         setCountdownVal(3);

//         // Limpiamos cualquier intervalo previo por seguridad
//         if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);

//         let count = 3;
//         timerIntervalRef.current = setInterval(() => {
//             count--;
//             if (count > 0) {
//                 setCountdownVal(count);
//             } else {
//                 // PASO 2: INICIAR JUEGO (Al llegar a 0)
//                 clearInterval(timerIntervalRef.current);
//                 runGameTimer(durationSeconds);
//             }
//         }, 1000);
//     };

//     const runGameTimer = (duration) => {
//         setGameState('RUNNING');
//         setTimeLeft(duration);

//         let localTime = duration;
        
//         timerIntervalRef.current = setInterval(() => {
//             localTime--;
//             setTimeLeft(localTime >= 0 ? localTime : 0);

//             if (localTime <= 0) {
//                 // Tiempo agotado localmente.
//                 // No mostramos ganador aún, solo detenemos el reloj.
//                 // El useEffect de arriba se encargará de mostrar el ganador cuando llegue del server.
//                 clearInterval(timerIntervalRef.current);
//                 // Opcional: Podrías poner un estado intermedio 'CALCULATING' si quieres
//             }
//         }, 1000);
//     };

//     // --------------------------------------------------------------------------------
//     // HELPERS VISUALES
//     // --------------------------------------------------------------------------------
//     const getZoneStyle = (zoneName) => {
//         const n = (zoneName || '').toLowerCase();
//         if (n.includes('azul')) return { colorClass: 'blue', hex: '#004d98' };
//         if (n.includes('oro')) return { colorClass: 'gold', hex: '#ffd700' };
//         if (n.includes('canels')) return { colorClass: 'blue', hex: '#0055a5' };
//         if (n.includes('tecate')) return { colorClass: 'red', hex: '#cb0909' };
//         if (n.includes('mobil')) return { colorClass: 'red', hex: '#ee1c25' };
//         if (n.includes('red cola')) return { colorClass: 'red', hex: '#da291c' };
//         if (n.includes('moctezuma')) return { colorClass: 'green', hex: '#005826' };
//         if (n.includes('nissan')) return { colorClass: 'gray', hex: '#666666' };
//         return { colorClass: 'red', hex: '#dc3545' };
//     };

//     const renderVersusMode = () => {
//         if (zones.length < 2) return null;
//         const zoneA = zones[0];
//         const zoneB = zones[1];
//         const styleA = getZoneStyle(zoneA.zone);
//         const styleB = getZoneStyle(zoneB.zone);

//         return (
//             <div className="noisemeter-screen__versus">
//                 <div className="noisemeter-screen__bar-container noisemeter-screen__bar-container--left">
//                     <div className="noisemeter-screen__bar-track">
//                         <div className={`noisemeter-screen__bar-fill noisemeter-screen__bar-fill--${styleA.colorClass}`} style={{ height: `${zoneA.progressPercent}%`, background: styleA.hex }}>
//                             <span className="noisemeter-screen__score">{Math.floor(zoneA.progressPercent)}%</span>
//                         </div>
//                     </div>
//                 </div>
//                 <div className="noisemeter-screen__vs-badge">VS</div>
//                 <div className="noisemeter-screen__bar-container noisemeter-screen__bar-container--right">
//                     <div className="noisemeter-screen__bar-track">
//                         <div className={`noisemeter-screen__bar-fill noisemeter-screen__bar-fill--${styleB.colorClass}`} style={{ height: `${zoneB.progressPercent}%`, background: styleB.hex }}>
//                             <span className="noisemeter-screen__score">{Math.floor(zoneB.progressPercent)}%</span>
//                         </div>
//                     </div>
//                 </div>
//             </div>
//         );
//     };

//     const renderMultiMode = () => {
//         return (
//             <div className="noisemeter-screen__multi">
//                 {zones.map((z) => {
//                     const style = getZoneStyle(z.zone);
//                     const labelFontSize = zones.length > 5 ? '1.2rem' : '1.8rem';
//                     const labelText = z.zone.replace(/Zona\s/i, ''); 
//                     return (
//                         <div key={z.zone} className="noisemeter-screen__multi-column">
//                             <div className="noisemeter-screen__multi-track">
//                                 <div className="noisemeter-screen__multi-fill" style={{ height: `${z.progressPercent}%`, background: style.hex, boxShadow: `0 0 20px ${style.hex}60` }}></div>
//                             </div>
//                             <p className="noisemeter-screen__label" style={{ fontSize: labelFontSize }}>{labelText}</p>
//                         </div>
//                     );
//                 })}
//             </div>
//         );
//     };

//     // --------------------------------------------------------------------------------
//     // RENDER UI
//     // --------------------------------------------------------------------------------
//     return (
//         <div className="noisemeter-screen">
//             <div className="noisemeter-screen__bg">
//                 {isActiveWomensMode ? (
//                     <img src='/img/selfiefan/Fondo-SelfieFan-Femenil.png' className='noisemeter-screen__bg-img' alt="Fondo Femenil" />
//                 ) : (
//                     <img src='/img/page/backgrounds/background.png' className='noisemeter-screen__bg-img' alt="Fondo Varonil" />
//                 )}
//             </div>

//             <div className='Noisemeter__Screen__Box'>
//                 {/* HEADER */}
//                 <div className="noisemeter-screen__header">
//                     <img src="/img/page/adsl-five.png" className="Noisemeter__Screen__Logo" alt="Logo" />
                    
//                     {/* TIMER DE JUEGO */}
//                     {gameState === 'RUNNING' && timeLeft > 0 && (
//                         <div className="Noisemeter__Screen__Timer">
//                             <i className="bi bi-hourglass-split"></i> {timeLeft} s
//                         </div>
//                     )}
//                 </div>

//                 <div className="noisemeter-screen__content">
                    
//                     {/* ESTADO DE ESPERA */}
//                     {gameState === 'WAITING' && (
//                         <p className="Noisemeter__Screen__Text--Title m-auto">Preparando dinámica...</p>
//                     )}

//                     {/* CUENTA REGRESIVA 3, 2, 1 */}
//                     {gameState === 'COUNTDOWN' && (
//                         <motion.div 
//                             key={countdownVal} 
//                             initial={{ scale: 0.5, opacity: 0 }}
//                             animate={{ scale: 1.5, opacity: 1 }}
//                             exit={{ scale: 2, opacity: 0 }}
//                             transition={{ duration: 0.5 }}
//                             style={{ 
//                                 fontSize: '15rem', 
//                                 fontWeight: '900', 
//                                 color: '#fff', 
//                                 textShadow: '0 0 50px rgba(255,255,255,0.8)',
//                                 zIndex: 100
//                             }}
//                         >
//                             {countdownVal}
//                         </motion.div>
//                     )}

//                     {/* JUEGO CORRIENDO (BARRAS) */}
//                     {gameState === 'RUNNING' && (
//                         <>
//                             {zones.length === 2 ? renderVersusMode() : renderMultiMode()}
//                         </>
//                     )}

//                     {/* FINALIZADO Y GANADOR */}
//                     {gameState === 'FINISHED' && (
//                         <>
//                             <div style={{opacity: 0.3, pointerEvents: 'none', filter: 'blur(5px)'}}>
//                                 {zones.length === 2 ? renderVersusMode() : renderMultiMode()}
//                             </div>

//                             {winner && (
//                                 <div className="Results__FullScreenOverlay">
//                                     <Confetti width={width} height={height} recycle={false} numberOfPieces={500} />
                                    
//                                     <motion.div initial={{ scale: 0 }} animate={{ scale: 1.12 }} transition={{ duration: 0.6, type: 'spring' }} style={{textAlign: 'center'}}>
//                                         <p><i className="bi bi-trophy-fill" style={{fontSize: '5rem', color: '#ffc107'}}></i></p>
                                        
//                                         {(() => {
//                                             if (winner === 'tie') {
//                                                 return <p className="Results__FullScreenOverlay__OneWinner">¡Ha habido un empate!</p>;
//                                             }
//                                             return (
//                                                 <>
//                                                     <p className="Results__FullScreenOverlay__Title" style={{fontSize: '3rem'}}>El ganador es...</p>
//                                                     <p className="Results__FullScreenOverlay__OneWinner">
//                                                         {winner.toUpperCase()}
//                                                     </p>
//                                                 </>
//                                             );
//                                         })()}
//                                     </motion.div>
//                                 </div>
//                             )}
//                         </>
//                     )}
//                 </div>
//             </div>

//             <footer className='SelfieFan__Screen__QR__Footer FooterCredits'>Hecho con ♥ por Interaktia | 2026</footer>
//         </div>
//     );
// };

// export { Noisemeter };
















// const Noisemeter = ({ data, isActiveWomensMode }) => { 
    
//     // --- ESTADOS VISUALES ---
//     // 'IDLE': Esperando (Logo o mensaje de espera)
//     // 'COUNTDOWN': 3, 2, 1
//     // 'GAME': Barras y tiempo corriendo
//     // 'FINISHED': Ganador y confeti
//     const [screenPhase, setScreenPhase] = useState('IDLE');
    
//     const [zones, setZones] = useState([]);
//     const [winner, setWinner] = useState(null);
    
//     // Valores numéricos para la UI
//     const [timeLeft, setTimeLeft] = useState(0);      
//     const [countdownVal, setCountdownVal] = useState(3);
    
//     // --- REFS DE CONTROL ---
//     // Guardamos el ID del match actual para no reiniciar el conteo en cada parpadeo de internet
//     const currentMatchIdRef = useRef(null); 
    
//     // Refs para intervalos (para poder limpiarlos forzosamente)
//     const timerIntervalRef = useRef(null);
//     const countdownIntervalRef = useRef(null);
//     const pollingRef = useRef(null);

//     const { innerWidth: width, innerHeight: height } = window;

//     // --------------------------------------------------------------------------------
//     // 1. ESCUCHA DE DATOS (WS o Props) Y DETECCIÓN DE CAMBIOS
//     // --------------------------------------------------------------------------------
//     useEffect(() => {
//         if (!data) return;
//         handleServerData(data);
//     }, [data]);

//     // Polling de respaldo (igual que antes, pero solo alimenta handleServerData)
//     useEffect(() => {
//         const fetchState = async () => {
//             const response = await BLGetScreenState();
//             if (response && response.success && response.data) {
//                 handleServerData(response.data);
//             }
//         };
//         // Polling rápido para fluidez en las barras
//         pollingRef.current = setInterval(fetchState, 500);
//         return () => clearInterval(pollingRef.current);
//     }, []);

//     // --------------------------------------------------------------------------------
//     // 2. CEREBRO: DECIDE QUÉ HACER CON LA DATA
//     // --------------------------------------------------------------------------------
//     const handleServerData = (serverData) => {
//         // A. Actualizar las barras siempre (esto no afecta el tiempo)
//         if (serverData.zones) setZones(serverData.zones);

//         // B. Detectar FIN DEL JUEGO (Prioridad Máxima)
//         if (serverData.winnerZone || serverData.status === 2 || serverData.status === 'Finished') {
//             if (screenPhase !== 'FINISHED') {
//                 stopLocalTimers();
//                 setWinner(serverData.winnerZone || "Ganador"); // Fallback text
//                 setScreenPhase('FINISHED');
//             }
//             return;
//         }

//         // C. Detectar INICIO DE JUEGO (El "Trigger")
//         const isRunning = serverData.status === 1 || serverData.status === 'Running' || (serverData.matchId && !serverData.winnerZone);
        
//         if (isRunning) {
//             // ¿Es una partida nueva? (Comparamos con la ref)
//             if (currentMatchIdRef.current !== serverData.matchId) {
//                 console.log("¡Nueva partida detectada! Iniciando secuencia local...");
                
//                 // 1. Guardamos el ID para no volver a entrar aquí
//                 currentMatchIdRef.current = serverData.matchId;
                
//                 // 2. Calculamos duración basada en la diferencia de fechas (pero solo UNA vez)
//                 // O usamos un default de 15s si falla el cálculo.
//                 let duration = 15; 
//                 if (serverData.startAt && serverData.endsAt) {
//                     const start = new Date(serverData.startAt).getTime();
//                     const end = new Date(serverData.endsAt).getTime();
//                     const calcDuration = Math.ceil((end - start) / 1000);
//                     if (calcDuration > 0 && calcDuration < 600) duration = calcDuration;
//                 }

//                 // 3. ¡DISPARAR SECUENCIA LOCAL!
//                 triggerLocalSequence(duration);
//             }
//         } else {
//             // Si el status es 0 o Waiting y no estamos en Finished...
//             if (screenPhase !== 'IDLE' && screenPhase !== 'FINISHED') {
//                 // Opcional: Si el admin pone "Reset", volvemos a IDLE
//                 // setScreenPhase('IDLE');
//                 // currentMatchIdRef.current = null;
//             }
//         }
//     };

//     // --------------------------------------------------------------------------------
//     // 3. SECUENCIA LOCAL (El "Show")
//     // --------------------------------------------------------------------------------
//     const triggerLocalSequence = (gameDurationSeconds) => {
//         // Limpiar cualquier cosa previa
//         stopLocalTimers();
//         setWinner(null);

//         // PASO 1: CUENTA REGRESIVA (3, 2, 1)
//         setScreenPhase('COUNTDOWN');
//         setCountdownVal(3);

//         let count = 3;
//         countdownIntervalRef.current = setInterval(() => {
//             count--;
//             if (count > 0) {
//                 setCountdownVal(count);
//             } else {
//                 // FIN DE CUENTA REGRESIVA -> INICIO DE JUEGO
//                 clearInterval(countdownIntervalRef.current);
//                 startGameTimer(gameDurationSeconds);
//             }
//         }, 1000);
//     };

//     const startGameTimer = (duration) => {
//         setScreenPhase('GAME');
//         setTimeLeft(duration);

//         // PASO 2: CRONÓMETRO DE JUEGO
//         let currentTimer = duration;
//         timerIntervalRef.current = setInterval(() => {
//             currentTimer--;
//             setTimeLeft(currentTimer);

//             if (currentTimer <= 0) {
//                 // TIEMPO AGOTADO LOCALMENTE
//                 clearInterval(timerIntervalRef.current);
//                 // No pasamos a FINISHED todavía, esperamos a que el backend diga quién ganó.
//                 // Pero podemos mostrar 0s
//             }
//         }, 1000);
//     };

//     const stopLocalTimers = () => {
//         if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
//         if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
//     };


//     // --------------------------------------------------------------------------------
//     // HELPERS VISUALES
//     // --------------------------------------------------------------------------------
//     const getZoneStyle = (zoneName) => {
//         const n = (zoneName || '').toLowerCase();
//         if (n.includes('azul')) return { colorClass: 'blue', hex: '#004d98' };
//         if (n.includes('oro')) return { colorClass: 'gold', hex: '#ffd700' };
//         if (n.includes('canels')) return { colorClass: 'blue', hex: '#0055a5' };
//         if (n.includes('tecate')) return { colorClass: 'red', hex: '#cb0909' };
//         if (n.includes('mobil')) return { colorClass: 'red', hex: '#ee1c25' };
//         if (n.includes('red cola')) return { colorClass: 'red', hex: '#da291c' };
//         if (n.includes('moctezuma')) return { colorClass: 'green', hex: '#005826' };
//         if (n.includes('nissan')) return { colorClass: 'gray', hex: '#666666' };
//         return { colorClass: 'red', hex: '#dc3545' };
//     };

//     // --------------------------------------------------------------------------------
//     // RENDERIZADO (Simplificado con la lógica de fases)
//     // --------------------------------------------------------------------------------
//     const renderVersusMode = () => {
//         if (zones.length < 2) return null;
//         const zoneA = zones[0];
//         const zoneB = zones[1];
//         const styleA = getZoneStyle(zoneA.zone);
//         const styleB = getZoneStyle(zoneB.zone);

//         return (
//             <div className="noisemeter-screen__versus">
//                 <div className="noisemeter-screen__bar-container noisemeter-screen__bar-container--left">
//                     <div className="noisemeter-screen__bar-track">
//                         <div className={`noisemeter-screen__bar-fill`} style={{ height: `${zoneA.progressPercent}%`, background: styleA.hex }}>
//                             <span className="noisemeter-screen__score">{Math.floor(zoneA.progressPercent)}%</span>
//                         </div>
//                     </div>
//                 </div>
//                 <div className="noisemeter-screen__vs-badge">VS</div>
//                 <div className="noisemeter-screen__bar-container noisemeter-screen__bar-container--right">
//                     <div className="noisemeter-screen__bar-track">
//                         <div className={`noisemeter-screen__bar-fill`} style={{ height: `${zoneB.progressPercent}%`, background: styleB.hex }}>
//                             <span className="noisemeter-screen__score">{Math.floor(zoneB.progressPercent)}%</span>
//                         </div>
//                     </div>
//                 </div>
//             </div>
//         );
//     };

//     const renderMultiMode = () => {
//         return (
//             <div className="noisemeter-screen__multi">
//                 {zones.map((z) => {
//                     const style = getZoneStyle(z.zone);
//                     const labelFontSize = zones.length > 5 ? '1.2rem' : '1.8rem';
//                     const labelText = z.zone.replace(/Zona\s/i, ''); 
//                     return (
//                         <div key={z.zone} className="noisemeter-screen__multi-column">
//                             <div className="noisemeter-screen__multi-track">
//                                 <div className="noisemeter-screen__multi-fill" style={{ height: `${z.progressPercent}%`, background: style.hex, boxShadow: `0 0 20px ${style.hex}60` }}></div>
//                             </div>
//                             <p className="noisemeter-screen__label" style={{ fontSize: labelFontSize }}>{labelText}</p>
//                         </div>
//                     );
//                 })}
//             </div>
//         );
//     };

//     return (
//         <div className="noisemeter-screen">
//             <div className="noisemeter-screen__bg">
//                 <img 
//                     src={isActiveWomensMode ? '/img/selfiefan/Fondo-SelfieFan-Femenil.png' : '/img/page/backgrounds/background.png'} 
//                     className='noisemeter-screen__bg-img' 
//                     alt="Fondo" 
//                 />
//             </div>

//             <div className='Noisemeter__Screen__Box'>
//                 {/* HEADER */}
//                 <div className="noisemeter-screen__header">
//                     <img src="/img/page/adsl-five.png" className="Noisemeter__Screen__Logo" alt="Logo" />
                    
//                     {/* TIMER: Solo visible en fase GAME */}
//                     {screenPhase === 'GAME' && timeLeft > 0 && (
//                         <div className="Noisemeter__Screen__Timer">
//                             <i className="bi bi-hourglass-split"></i> {timeLeft} s
//                         </div>
//                     )}
//                 </div>

//                 <div className="noisemeter-screen__content">
                    
//                     {/* FASE: IDLE / WAITING */}
//                     {screenPhase === 'IDLE' && (
//                         <p className="Noisemeter__Screen__Text--Title m-auto">Preparando dinámica...</p>
//                     )}

//                     {/* FASE: COUNTDOWN */}
//                     <AnimatePresence>
//                         {screenPhase === 'COUNTDOWN' && (
//                             <motion.div 
//                                 key={countdownVal} // Key cambio fuerza re-render animación
//                                 initial={{ scale: 0.5, opacity: 0 }}
//                                 animate={{ scale: 1.5, opacity: 1 }}
//                                 exit={{ scale: 2, opacity: 0 }}
//                                 transition={{ duration: 0.5 }}
//                                 style={{ 
//                                     fontSize: '15rem', fontWeight: '900', color: '#fff', 
//                                     textShadow: '0 0 50px rgba(255,255,255,0.8)', zIndex: 100,
//                                     position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)'
//                                 }}
//                             >
//                                 {countdownVal}
//                             </motion.div>
//                         )}
//                     </AnimatePresence>

//                     {/* FASE: GAME (BARRAS) */}
//                     {screenPhase === 'GAME' && (
//                         <>
//                             {zones.length === 2 ? renderVersusMode() : renderMultiMode()}
//                         </>
//                     )}

//                     {/* FASE: FINISHED */}
//                     {screenPhase === 'FINISHED' && (
//                         <>
//                             {/* Dejamos las barras de fondo desenfocadas */}
//                             <div style={{opacity: 0.3, pointerEvents: 'none', filter: 'blur(5px)'}}>
//                                 {zones.length === 2 ? renderVersusMode() : renderMultiMode()}
//                             </div>

//                             {/* Overlay de Ganador */}
//                             {winner && (
//                                 <div className="Results__FullScreenOverlay">
//                                     <Confetti width={width} height={height} recycle={false} numberOfPieces={500} />
                                    
//                                     <motion.div initial={{ scale: 0 }} animate={{ scale: 1.12 }} transition={{ duration: 0.6, type: 'spring' }} style={{textAlign: 'center'}}>
//                                         <p><i className="bi bi-trophy-fill" style={{fontSize: '5rem', color: '#ffc107'}}></i></p>
                                        
//                                         {winner === 'tie' ? (
//                                             <p className="Results__FullScreenOverlay__OneWinner">¡Ha habido un empate!</p>
//                                         ) : (
//                                             <>
//                                                 <p className="Results__FullScreenOverlay__Title" style={{fontSize: '3rem'}}>El ganador es...</p>
//                                                 <p className="Results__FullScreenOverlay__OneWinner">{winner.toUpperCase()}</p>
//                                             </>
//                                         )}
//                                     </motion.div>
//                                 </div>
//                             )}
//                         </>
//                     )}
//                 </div>
//             </div>

//             <footer className='SelfieFan__Screen__QR__Footer FooterCredits'>Hecho con ♥ por Interaktia | 2026</footer>
//         </div>
//     );
// };

// export { Noisemeter };




















// const Noisemeter = ({ data, isActiveWomensMode }) => { 
    
//     // --- ESTADOS ---
//     const [gameState, setGameState] = useState('Waiting');
//     const [zones, setZones] = useState([]);
//     const [winner, setWinner] = useState(null);
    
//     const [internalPhase, setInternalPhase] = useState('PRE_GAME'); 

//     const [timeLeft, setTimeLeft] = useState(0);      
//     const [countdownVal, setCountdownVal] = useState(0); 
    
//     const [startAtTimestamp, setStartAtTimestamp] = useState(null);
//     const [endsAtTimestamp, setEndsAtTimestamp] = useState(null);

//     const pollingRef = useRef(null);
//     const timerIntervalRef = useRef(null);

//     const { innerWidth: width, innerHeight: height } = window;

//     // --------------------------------------------------------------------------------
//     // 1. SINCRONIZACIÓN DE DATOS
//     // --------------------------------------------------------------------------------
    
//     useEffect(() => {
//         if (data) {
//             // FIX: Forzamos parseo seguro de fechas. 
//             // Si viene string, new Date lo maneja, pero si hay lío de zona horaria, esto ayuda.
//             if (data.startAt) setStartAtTimestamp(new Date(data.startAt).getTime());
//             if (data.endsAt) setEndsAtTimestamp(new Date(data.endsAt).getTime());

//             if (data.matchId && !data.winnerZone) setGameState('Running');
            
//             if (data.winnerZone) {
//                 setGameState('Finished');
//                 setWinner(data.winnerZone);
//                 if (data.zones) setZones(data.zones);
//             }
//         }
//     }, [data]);

//     useEffect(() => {
//         const updateStateFromApi = async () => {
//             // Aquí asumo que BLGetScreenState existe y funciona
//             const response = await BLGetScreenState();
//             if (response && response.success && response.data) {
//                 const apiData = response.data;
                
//                 let statusStr = 'Waiting';
//                 if (apiData.status === 1 || apiData.status === 'Running') statusStr = 'Running';
//                 if (apiData.status === 2 || apiData.status === 'Finished') statusStr = 'Finished';

//                 setGameState(prev => (prev === 'Finished' ? 'Finished' : statusStr));
//                 setZones(apiData.zones || []);
                
//                 if (apiData.startAt) setStartAtTimestamp(new Date(apiData.startAt).getTime());
//                 if (apiData.endsAt) setEndsAtTimestamp(new Date(apiData.endsAt).getTime());

//                 if (statusStr === 'Finished' || (apiData.winnerZone && apiData.winnerZone !== 'none')) {
//                     setWinner(apiData.winnerZone);
//                     setGameState('Finished');
//                 }
//             }
//         };

//         updateStateFromApi();
//         pollingRef.current = setInterval(updateStateFromApi, 500); 
//         return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
//     }, []);

//     // --------------------------------------------------------------------------------
//     // 2. LÓGICA DE TEMPORIZADORES (CORREGIDA PARA PRODUCCIÓN)
//     // --------------------------------------------------------------------------------
//     useEffect(() => {
//         const updateTimers = () => {
//             const now = Date.now();

//             // --- FIX CRÍTICO ---
//             // Si no tenemos timestamps, no hacemos nada
//             if (!startAtTimestamp || !endsAtTimestamp) return;

//             // Calculamos diferencias puras
//             const diffStart = Math.ceil((startAtTimestamp - now) / 1000);
//             const diffEnd = Math.ceil((endsAtTimestamp - now) / 1000);

//             // DETECCIÓN DE DESFASE DE RELOJ:
//             // Si el servidor dice que empieza en el futuro (ej. 300 segundos) 
//             // PERO el estado del juego ya es 'Running', significa que hay desface de hora.
//             // Si la diferencia es mayor a 5 segundos y estamos en Running, forzamos el inicio.
            
//             const isFutureStart = diffStart > 0;
//             const isHugeLag = diffStart > 5; // Tolerancia de 5 segundos

//             // LÓGICA DE FASES
            
//             // CASO A: Realmente es la cuenta regresiva (3, 2, 1)
//             // Solo entramos aquí si es futuro cercano (< 5s) o si el estado NO es Running forzado
//             if (isFutureStart && (!isHugeLag || gameState !== 'Running')) {
//                 setInternalPhase('PRE_GAME');
//                 // Si el lag es gigante (ej 303), mostramos 3 para no asustar, o el valor real si es pequeño
//                 setCountdownVal(isHugeLag ? 3 : diffStart); 
//                 return;
//             }

//             // CASO B: Juego Corriendo (O corrección de error de reloj)
//             // Si ya pasamos la hora de inicio O si hay un lag gigante pero el juego debe estar corriendo
//             setInternalPhase('GAME');
            
//             // Clamp de seguridad para el tiempo restante (0 a 600s)
//             // Si diffEnd es negativo (ya acabó), ponemos 0.
//             const safeTime = (diffEnd > 0 && diffEnd < 600) ? diffEnd : 0;
//             setTimeLeft(safeTime);

//             if (diffEnd <= 0 && timerIntervalRef.current) {
//                 // Opcional: Podríamos autocompletar a finished aquí, 
//                 // pero mejor esperamos al socket/polling para cambiar gameState
//                 // clearInterval(timerIntervalRef.current); // No detengas, deja que fluya hasta que cambie el estado
//             }
//         };

//         // El timer corre si estamos en estado "Running" y tenemos fechas
//         if (gameState === 'Running' && endsAtTimestamp) {
//             if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
            
//             updateTimers(); // Ejecutar inmediato
//             timerIntervalRef.current = setInterval(updateTimers, 250);
//         } else {
//             if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
//             if (gameState === 'Finished') setTimeLeft(0);
//         }

//         return () => { if (timerIntervalRef.current) clearInterval(timerIntervalRef.current); };
//     }, [gameState, startAtTimestamp, endsAtTimestamp]); // Dependencias clave


//     // --------------------------------------------------------------------------------
//     // HELPERS VISUALES
//     // --------------------------------------------------------------------------------
//     const getZoneStyle = (zoneName) => {
//         const n = (zoneName || '').toLowerCase();
//         if (n.includes('azul')) return { colorClass: 'blue', hex: '#004d98' };
//         if (n.includes('oro')) return { colorClass: 'gold', hex: '#ffd700' };
//         if (n.includes('canels')) return { colorClass: 'blue', hex: '#0055a5' };
//         if (n.includes('tecate')) return { colorClass: 'red', hex: '#cb0909' };
//         if (n.includes('mobil')) return { colorClass: 'red', hex: '#ee1c25' };
//         if (n.includes('red cola')) return { colorClass: 'red', hex: '#da291c' };
//         if (n.includes('moctezuma')) return { colorClass: 'green', hex: '#005826' };
//         if (n.includes('nissan')) return { colorClass: 'gray', hex: '#666666' };
//         return { colorClass: 'red', hex: '#dc3545' };
//     };

//     // --------------------------------------------------------------------------------
//     // RENDERIZADO BARRAS
//     // --------------------------------------------------------------------------------
//     const renderVersusMode = () => {
//         if (zones.length < 2) return null;
//         const zoneA = zones[0];
//         const zoneB = zones[1];
//         const styleA = getZoneStyle(zoneA.zone);
//         const styleB = getZoneStyle(zoneB.zone);

//         return (
//             <div className="noisemeter-screen__versus">
//                 <div className="noisemeter-screen__bar-container noisemeter-screen__bar-container--left">
//                     <div className="noisemeter-screen__bar-track">
//                         <div className={`noisemeter-screen__bar-fill noisemeter-screen__bar-fill--${styleA.colorClass}`} style={{ height: `${zoneA.progressPercent}%`, background: styleA.hex }}>
//                             <span className="noisemeter-screen__score">{Math.floor(zoneA.progressPercent)}%</span>
//                         </div>
//                     </div>
//                 </div>
//                 <div className="noisemeter-screen__vs-badge">VS</div>
//                 <div className="noisemeter-screen__bar-container noisemeter-screen__bar-container--right">
//                     <div className="noisemeter-screen__bar-track">
//                         <div className={`noisemeter-screen__bar-fill noisemeter-screen__bar-fill--${styleB.colorClass}`} style={{ height: `${zoneB.progressPercent}%`, background: styleB.hex }}>
//                             <span className="noisemeter-screen__score">{Math.floor(zoneB.progressPercent)}%</span>
//                         </div>
//                     </div>
//                 </div>
//             </div>
//         );
//     };

//     const renderMultiMode = () => {
//         return (
//             <div className="noisemeter-screen__multi">
//                 {zones.map((z) => {
//                     const style = getZoneStyle(z.zone);
//                     const labelFontSize = zones.length > 5 ? '1.2rem' : '1.8rem';
//                     const labelText = z.zone.replace(/Zona\s/i, ''); 
//                     return (
//                         <div key={z.zone} className="noisemeter-screen__multi-column">
//                             <div className="noisemeter-screen__multi-track">
//                                 <div className="noisemeter-screen__multi-fill" style={{ height: `${z.progressPercent}%`, background: style.hex, boxShadow: `0 0 20px ${style.hex}60` }}></div>
//                             </div>
//                             <p className="noisemeter-screen__label" style={{ fontSize: labelFontSize }}>{labelText}</p>
//                         </div>
//                     );
//                 })}
//             </div>
//         );
//     };

//     // --------------------------------------------------------------------------------
//     // RENDER PRINCIPAL
//     // --------------------------------------------------------------------------------
//     return (
//         <div className="noisemeter-screen">
//             <div className="noisemeter-screen__bg">
//                 {isActiveWomensMode ? (
//                     <img src='/img/selfiefan/Fondo-SelfieFan-Femenil.png' className='noisemeter-screen__bg-img' alt="Fondo Femenil" />
//                 ) : (
//                     <img src='/img/page/backgrounds/background.png' className='noisemeter-screen__bg-img' alt="Fondo Varonil" />
//                 )}
//             </div>

//             <div className='Noisemeter__Screen__Box'>
//                 {/* HEADER */}
//                 <div className="noisemeter-screen__header">
//                     <img src="/img/page/adsl-five.png" className="Noisemeter__Screen__Logo" alt="Logo" />
                    
//                     {/* TIMER DE JUEGO */}
//                     {gameState === 'Running' && internalPhase === 'GAME' && timeLeft > 0 && (
//                         <div className="Noisemeter__Screen__Timer">
//                             <i className="bi bi-hourglass-split"></i> {timeLeft} s
//                         </div>
//                     )}
//                 </div>

//                 <div className="noisemeter-screen__content">
//                     {gameState === 'Waiting' && (
//                         <p className="Noisemeter__Screen__Text--Title m-auto">Preparando dinámica...</p>
//                     )}

//                     {(gameState === 'Running') && (
//                         <>
//                             {/* FASE A: CUENTA REGRESIVA 3..2..1 */}
//                             {internalPhase === 'PRE_GAME' && (
//                                 <motion.div 
//                                     key={countdownVal} 
//                                     initial={{ scale: 0.5, opacity: 0 }}
//                                     animate={{ scale: 1.5, opacity: 1 }}
//                                     exit={{ scale: 2, opacity: 0 }}
//                                     transition={{ duration: 0.5 }}
//                                     style={{ 
//                                         fontSize: '15rem', 
//                                         fontWeight: '900', 
//                                         color: '#fff', 
//                                         textShadow: '0 0 50px rgba(255,255,255,0.8)',
//                                         zIndex: 100
//                                     }}
//                                 >
//                                     {countdownVal}
//                                 </motion.div>
//                             )}

//                             {/* FASE B: JUEGO ACTIVO (BARRAS) */}
//                             {internalPhase === 'GAME' && (
//                                 <>
//                                     {zones.length === 2 ? renderVersusMode() : renderMultiMode()}
//                                 </>
//                             )}
//                         </>
//                     )}

//                     {/* ESTADO FINALIZADO */}
//                     {gameState === 'Finished' && (
//                         <>
//                             <div style={{opacity: 0.3, pointerEvents: 'none', filter: 'blur(5px)'}}>
//                                 {zones.length === 2 ? renderVersusMode() : renderMultiMode()}
//                             </div>
//                         </>
//                     )}

//                     {/* GANADOR OVERLAY */}
//                     {gameState === 'Finished' && winner && (
//                         <div className="Results__FullScreenOverlay">
//                             <Confetti width={width} height={height} recycle={false} numberOfPieces={500} />
                            
//                             <motion.div initial={{ scale: 0 }} animate={{ scale: 1.12 }} transition={{ duration: 0.6, type: 'spring' }} style={{textAlign: 'center'}}>
//                                 <p><i className="bi bi-trophy-fill" style={{fontSize: '5rem', color: '#ffc107'}}></i></p>
                                
//                                 {(() => {
//                                     if (winner === 'tie') {
//                                         return <p className="Results__FullScreenOverlay__OneWinner">¡Ha habido un empate!</p>;
//                                     }
//                                     return (
//                                         <>
//                                             <p className="Results__FullScreenOverlay__Title" style={{fontSize: '3rem'}}>El ganador es...</p>
//                                             <p className="Results__FullScreenOverlay__OneWinner">
//                                                 {winner.toUpperCase()}
//                                             </p>
//                                         </>
//                                     );
//                                 })()}
//                             </motion.div>
//                         </div>
//                     )}
//                 </div>
//             </div>

//             <footer className='SelfieFan__Screen__QR__Footer FooterCredits'>Hecho con ♥ por Interaktia | 2026</footer>
//         </div>
//     );
// };

// export { Noisemeter };
















// /**
//  * Componente de Pantalla Gigante para el Ruidómetro.
//  * Incluye: Cuenta regresiva (3,2,1), Barras en vivo, Temporizador de juego y Ganador.
//  */
// const Noisemeter = ({ data, isActiveWomensMode }) => { 
    
//     // --- ESTADOS ---
//     const [gameState, setGameState] = useState('Waiting');
//     const [zones, setZones] = useState([]);
//     const [winner, setWinner] = useState(null);
    
//     // Estado para diferenciar "Cuenta Regresiva Inicial" vs "Juego Corriendo"
//     // 'PRE_GAME' = Mostrando 3, 2, 1...
//     // 'GAME'     = Mostrando barras y tiempo restante
//     const [internalPhase, setInternalPhase] = useState('PRE_GAME'); 

//     // Valores numéricos para la UI
//     const [timeLeft, setTimeLeft] = useState(0);      // Tiempo de juego restante
//     const [countdownVal, setCountdownVal] = useState(0); // Valor del 3, 2, 1
    
//     // Timestamps de referencia
//     const [startAtTimestamp, setStartAtTimestamp] = useState(null);
//     const [endsAtTimestamp, setEndsAtTimestamp] = useState(null);

//     // Refs
//     const pollingRef = useRef(null);
//     const timerIntervalRef = useRef(null);

//     const { innerWidth: width, innerHeight: height } = window;

//     // --------------------------------------------------------------------------------
//     // 1. SINCRONIZACIÓN DE DATOS
//     // --------------------------------------------------------------------------------
    
//     // Reacción inmediata (WS)
//     useEffect(() => {
//         if (data) {
//             if (data.matchId && !data.winnerZone) setGameState('Running');
            
//             // Guardamos ambos tiempos: Inicio y Fin
//             if (data.startAt) setStartAtTimestamp(new Date(data.startAt).getTime());
//             if (data.endsAt) setEndsAtTimestamp(new Date(data.endsAt).getTime());

//             if (data.winnerZone) {
//                 setGameState('Finished');
//                 setWinner(data.winnerZone);
//                 if (data.zones) setZones(data.zones);
//             }
//         }
//     }, [data]);

//     // Polling periódico (API)
//     useEffect(() => {
//         const updateStateFromApi = async () => {
//             const response = await BLGetScreenState();
//             if (response.success && response.data) {
//                 const apiData = response.data;
                
//                 let statusStr = 'Waiting';
//                 if (apiData.status === 1 || apiData.status === 'Running') statusStr = 'Running';
//                 if (apiData.status === 2 || apiData.status === 'Finished') statusStr = 'Finished';

//                 setGameState(prev => (prev === 'Finished' ? 'Finished' : statusStr));
//                 setZones(apiData.zones || []);
                
//                 if (apiData.startAt) setStartAtTimestamp(new Date(apiData.startAt).getTime());
//                 if (apiData.endsAt) setEndsAtTimestamp(new Date(apiData.endsAt).getTime());

//                 if (statusStr === 'Finished' || (apiData.winnerZone && apiData.winnerZone !== 'none')) {
//                     setWinner(apiData.winnerZone);
//                     setGameState('Finished');
//                 }
//             }
//         };

//         updateStateFromApi();
//         pollingRef.current = setInterval(updateStateFromApi, 500); 
//         return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
//     }, []);

//     // --------------------------------------------------------------------------------
//     // 2. LÓGICA DE TEMPORIZADORES (3..2..1  Y  Tiempo de Juego)
//     // --------------------------------------------------------------------------------
//     useEffect(() => {
//         const updateTimers = () => {
//             const now = Date.now();

//             // CASO A: Aún no empieza el juego (Cuenta Regresiva 3, 2, 1)
//             if (startAtTimestamp && now < startAtTimestamp) {
//                 setInternalPhase('PRE_GAME');
//                 const diffStart = Math.ceil((startAtTimestamp - now) / 1000);
//                 setCountdownVal(diffStart > 0 ? diffStart : 1);
//                 return;
//             }

//             // CASO B: Ya empezó (Juego Corriendo)
//             if (endsAtTimestamp && now >= startAtTimestamp) {
//                 setInternalPhase('GAME');
//                 const diffEnd = Math.ceil((endsAtTimestamp - now) / 1000);
                
//                 // Clamp de seguridad (0 a 600s)
//                 const safeTime = (diffEnd > 0 && diffEnd < 600) ? diffEnd : 0;
//                 setTimeLeft(safeTime);

//                 if (diffEnd <= 0 && timerIntervalRef.current) {
//                     clearInterval(timerIntervalRef.current);
//                 }
//             }
//         };

//         // El timer corre si estamos en estado "Running" (según el backend) y tenemos fechas
//         if (gameState === 'Running' && endsAtTimestamp) {
//             if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
            
//             updateTimers(); // Ejecutar inmediato
//             timerIntervalRef.current = setInterval(updateTimers, 250); // Frecuencia alta para precisión visual
//         } else {
//             if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
//             if (gameState === 'Finished') setTimeLeft(0);
//         }

//         return () => { if (timerIntervalRef.current) clearInterval(timerIntervalRef.current); };
//     }, [gameState, startAtTimestamp, endsAtTimestamp]);


//     // --------------------------------------------------------------------------------
//     // HELPERS VISUALES
//     // --------------------------------------------------------------------------------
//     const getZoneStyle = (zoneName) => {
//         const n = (zoneName || '').toLowerCase();
//         if (n.includes('azul')) return { colorClass: 'blue', hex: '#004d98' };
//         if (n.includes('oro')) return { colorClass: 'gold', hex: '#ffd700' };
//         if (n.includes('canels')) return { colorClass: 'blue', hex: '#0055a5' };
//         if (n.includes('tecate')) return { colorClass: 'red', hex: '#cb0909' };
//         if (n.includes('mobil')) return { colorClass: 'red', hex: '#ee1c25' };
//         if (n.includes('red cola')) return { colorClass: 'red', hex: '#da291c' };
//         if (n.includes('moctezuma')) return { colorClass: 'green', hex: '#005826' };
//         if (n.includes('nissan')) return { colorClass: 'gray', hex: '#666666' };
//         return { colorClass: 'red', hex: '#dc3545' };
//     };

//     // --------------------------------------------------------------------------------
//     // RENDERIZADO BARRAS
//     // --------------------------------------------------------------------------------
//     const renderVersusMode = () => {
//         if (zones.length < 2) return null;
//         const zoneA = zones[0];
//         const zoneB = zones[1];
//         const styleA = getZoneStyle(zoneA.zone);
//         const styleB = getZoneStyle(zoneB.zone);

//         return (
//             <div className="noisemeter-screen__versus">
//                 <div className="noisemeter-screen__bar-container noisemeter-screen__bar-container--left">
//                     {/* <div className="noisemeter-screen__label">{zoneA.zone}</div> */}
//                     <div className="noisemeter-screen__bar-track">
//                         <div className={`noisemeter-screen__bar-fill noisemeter-screen__bar-fill--${styleA.colorClass}`} style={{ height: `${zoneA.progressPercent}%`, background: styleA.hex }}>
//                             <span className="noisemeter-screen__score">{Math.floor(zoneA.progressPercent)}%</span>
//                         </div>
//                     </div>
//                 </div>
//                 <div className="noisemeter-screen__vs-badge">VS</div>
//                 <div className="noisemeter-screen__bar-container noisemeter-screen__bar-container--right">
//                     {/* <div className="noisemeter-screen__label">{zoneB.zone}</div> */}
//                     <div className="noisemeter-screen__bar-track">
//                         <div className={`noisemeter-screen__bar-fill noisemeter-screen__bar-fill--${styleB.colorClass}`} style={{ height: `${zoneB.progressPercent}%`, background: styleB.hex }}>
//                             <span className="noisemeter-screen__score">{Math.floor(zoneB.progressPercent)}%</span>
//                         </div>
//                     </div>
//                 </div>
//             </div>
//         );
//     };

//     const renderMultiMode = () => {
//         return (
//             <div className="noisemeter-screen__multi">
//                 {zones.map((z) => {
//                     const style = getZoneStyle(z.zone);
//                     const labelFontSize = zones.length > 5 ? '1.2rem' : '1.8rem';
//                     const labelText = z.zone.replace(/Zona\s/i, ''); 
//                     return (
//                         <div key={z.zone} className="noisemeter-screen__multi-column">
//                             <div className="noisemeter-screen__multi-track">
//                                 <div className="noisemeter-screen__multi-fill" style={{ height: `${z.progressPercent}%`, background: style.hex, boxShadow: `0 0 20px ${style.hex}60` }}></div>
//                             </div>
//                             <p className="noisemeter-screen__label" style={{ fontSize: labelFontSize }}>{labelText}</p>
//                         </div>
//                     );
//                 })}
//             </div>
//         );
//     };

//     // --------------------------------------------------------------------------------
//     // RENDER PRINCIPAL
//     // --------------------------------------------------------------------------------
//     return (
//         <div className="noisemeter-screen">
//             <div className="noisemeter-screen__bg">
//                 {isActiveWomensMode ? (
//                     <img src='/img/selfiefan/Fondo-SelfieFan-Femenil.png' className='noisemeter-screen__bg-img' alt="Fondo Femenil" />
//                 ) : (
//                     <img src='/img/page/backgrounds/background.png' className='noisemeter-screen__bg-img' alt="Fondo Varonil" />
//                 )}
//             </div>

//             <div className='Noisemeter__Screen__Box'>
//                 {/* HEADER */}
//                 <div className="noisemeter-screen__header">
//                     <img src="/img/page/adsl-five.png" className="Noisemeter__Screen__Logo" alt="Logo" />
                    
//                     {/* TIMER DE JUEGO (Solo visible si ya pasó la cuenta regresiva) */}
//                     {gameState === 'Running' && internalPhase === 'GAME' && timeLeft > 0 && (
//                         <div className="Noisemeter__Screen__Timer">
//                             <i className="bi bi-hourglass-split"></i> {timeLeft} s
//                         </div>
//                     )}
//                 </div>

//                 <div className="noisemeter-screen__content">
//                     {gameState === 'Waiting' && (
//                         <p className="Noisemeter__Screen__Text--Title m-auto">Preparando dinámica...</p>
//                     )}

//                     {/* LÓGICA DE VISUALIZACIÓN DURANTE "RUNNING" */}
//                     {(gameState === 'Running') && (
//                         <>
//                             {/* FASE A: CUENTA REGRESIVA 3..2..1 */}
//                             {internalPhase === 'PRE_GAME' && (
//                                 <motion.div 
//                                     key={countdownVal} // Key para reiniciar animación al cambiar número
//                                     initial={{ scale: 0.5, opacity: 0 }}
//                                     animate={{ scale: 1.5, opacity: 1 }}
//                                     exit={{ scale: 2, opacity: 0 }}
//                                     transition={{ duration: 0.5 }}
//                                     style={{ 
//                                         fontSize: '15rem', 
//                                         fontWeight: '900', 
//                                         color: '#fff', 
//                                         textShadow: '0 0 50px rgba(255,255,255,0.8)',
//                                         zIndex: 100
//                                     }}
//                                 >
//                                     {countdownVal}
//                                 </motion.div>
//                             )}

//                             {/* FASE B: JUEGO ACTIVO (BARRAS) */}
//                             {internalPhase === 'GAME' && (
//                                 <>
//                                     {zones.length === 2 ? renderVersusMode() : renderMultiMode()}
//                                 </>
//                             )}
//                         </>
//                     )}

//                     {/* ESTADO FINALIZADO */}
//                     {gameState === 'Finished' && (
//                         <>
//                             {/* Si acaba de terminar, mostramos las barras un momento antes del winner (opcional) o directo el winner */}
//                             {/* Aquí dejamos las barras de fondo y el overlay encima */}
//                             <div style={{opacity: 0.3, pointerEvents: 'none', filter: 'blur(5px)'}}>
//                                 {zones.length === 2 ? renderVersusMode() : renderMultiMode()}
//                             </div>
//                         </>
//                     )}

//                     {/* GANADOR OVERLAY */}
//                     {gameState === 'Finished' && winner && (
//                         <div className="Results__FullScreenOverlay">
//                             <Confetti width={width} height={height} recycle={false} numberOfPieces={500} />
                            
//                             <motion.div initial={{ scale: 0 }} animate={{ scale: 1.12 }} transition={{ duration: 0.6, type: 'spring' }} style={{textAlign: 'center'}}>
//                                 <p><i className="bi bi-trophy-fill" style={{fontSize: '5rem', color: '#ffc107'}}></i></p>
                                
//                                 {(() => {
//                                     if (winner === 'tie') {
//                                         return <p className="Results__FullScreenOverlay__OneWinner">¡Ha habido un empate!</p>;
//                                     }
//                                     return (
//                                         <>
//                                             <p className="Results__FullScreenOverlay__Title" style={{fontSize: '3rem'}}>El ganador es...</p>
//                                             <p className="Results__FullScreenOverlay__OneWinner">
//                                                 {winner.toUpperCase()}
//                                             </p>
//                                         </>
//                                     );
//                                 })()}
//                             </motion.div>
//                         </div>
//                     )}
//                 </div>
//             </div>

//             <footer className='SelfieFan__Screen__QR__Footer FooterCredits'>Hecho con ♥ por Interaktia | 2026</footer>
//         </div>
//     );
// };

// export { Noisemeter };






















// Funcional
// /**
//  * Componente de Pantalla Gigante para el Ruidómetro.
//  * Incluye: Barras en vivo, Temporizador y Pantalla de Ganador con Confeti.
//  */
// const Noisemeter = ({ data, isActiveWomensMode }) => { 
    
//     // --- ESTADOS ---
//     const [gameState, setGameState] = useState('Waiting');
//     const [zones, setZones] = useState([]);
//     const [winner, setWinner] = useState(null);
    
//     // Estado para el temporizador visual
//     const [timeLeft, setTimeLeft] = useState(0); 
//     const [endsAtTimestamp, setEndsAtTimestamp] = useState(null);

//     // Refs
//     const pollingRef = useRef(null);
//     const timerIntervalRef = useRef(null);

//     // Dimensiones para el confeti
//     const { innerWidth: width, innerHeight: height } = window;

//     // --------------------------------------------------------------------------------
//     // 1. SINCRONIZACIÓN DE DATOS (Polling + Websocket)
//     // --------------------------------------------------------------------------------
    
//     // Reacción inmediata (WS)
//     useEffect(() => {
//         if (data) {
//             if (data.matchId && !data.winnerZone) setGameState('Running');
            
//             // Si llega fecha de fin, actualizamos timestamp para el timer
//             if (data.endsAt) setEndsAtTimestamp(new Date(data.endsAt).getTime());

//             if (data.winnerZone) {
//                 setGameState('Finished');
//                 setWinner(data.winnerZone);
//                 if (data.zones) setZones(data.zones);
//             }
//         }
//     }, [data]);

//     // Polling periódico (API)
//     useEffect(() => {
//         const updateStateFromApi = async () => {
//             const response = await BLGetScreenState();
//             if (response.success && response.data) {
//                 const apiData = response.data;
                
//                 let statusStr = 'Waiting';
//                 if (apiData.status === 1 || apiData.status === 'Running') statusStr = 'Running';
//                 if (apiData.status === 2 || apiData.status === 'Finished') statusStr = 'Finished';

//                 setGameState(prev => (prev === 'Finished' ? 'Finished' : statusStr));
//                 setZones(apiData.zones || []);
                
//                 // Actualizar referencia de tiempo de fin
//                 if (apiData.endsAt) setEndsAtTimestamp(new Date(apiData.endsAt).getTime());

//                 if (statusStr === 'Finished' || (apiData.winnerZone && apiData.winnerZone !== 'none')) {
//                     setWinner(apiData.winnerZone);
//                     setGameState('Finished');
//                 }
//             }
//         };

//         updateStateFromApi();
//         pollingRef.current = setInterval(updateStateFromApi, 500); 
//         return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
//     }, []);

//     // // --------------------------------------------------------------------------------
//     // // 2. LÓGICA DEL TEMPORIZADOR
//     // // --------------------------------------------------------------------------------
//     // useEffect(() => {
//     //     // Solo corre el timer si estamos en juego y tenemos fecha de fin
//     //     if (gameState === 'Running' && endsAtTimestamp) {
//     //         timerIntervalRef.current = setInterval(() => {
//     //             const now = Date.now();
//     //             const diff = Math.ceil((endsAtTimestamp - now) / 1000);
                
//     //             // Evitamos números negativos
//     //             setTimeLeft(diff > 0 ? diff : 0);

//     //             if (diff <= 0) clearInterval(timerIntervalRef.current);
//     //         }, 1000);
//     //     } else {
//     //         // Si termina o está en espera, limpiamos
//     //         if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
//     //         if (gameState === 'Finished') setTimeLeft(0);
//     //     }

//     //     return () => { if (timerIntervalRef.current) clearInterval(timerIntervalRef.current); };
//     // }, [gameState, endsAtTimestamp]);

//     // --------------------------------------------------------------------------------
//     // 2. LÓGICA DEL TEMPORIZADOR (CORREGIDA)
//     // --------------------------------------------------------------------------------
//     useEffect(() => {
//         const updateTimer = () => {
//             if (!endsAtTimestamp) return;

//             const now = Date.now();
//             const diff = Math.ceil((endsAtTimestamp - now) / 1000);

//             // VALIDACIÓN DE SEGURIDAD:
//             // 1. diff > 0: Evita negativos.
//             // 2. diff < 600: Si el tiempo es mayor a 600seg (10 min), asumimos que hay un 
//             //    error de sincronización horaria (timezone) y forzamos a 0 para no romper la UI.
//             const safeTime = (diff > 0 && diff < 600) ? diff : 0;

//             setTimeLeft(safeTime);

//             // Si llegamos a 0, detenemos el reloj
//             if (diff <= 0) {
//                 if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
//             }
//         };

//         // Solo corre el timer si estamos en juego y tenemos fecha de fin
//         if (gameState === 'Running' && endsAtTimestamp) {
//             // A) Limpiar intervalo previo si existe
//             if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);

//             // B) ¡IMPORTANTE! Ejecutar una vez INMEDIATAMENTE
//             // Esto corrige que al inicio se vea un número raro por 1 segundo.
//             updateTimer();

//             // C) Iniciar el intervalo
//             timerIntervalRef.current = setInterval(updateTimer, 1000);
//         } else {
//             // Si termina o está en espera, limpiamos y reseteamos visualmente
//             if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
            
//             // Opcional: Si el juego terminó, asegurar que se vea 0
//             if (gameState === 'Finished') setTimeLeft(0);
//         }

//         return () => { if (timerIntervalRef.current) clearInterval(timerIntervalRef.current); };
//     }, [gameState, endsAtTimestamp]);


//     // --------------------------------------------------------------------------------
//     // HELPERS VISUALES
//     // --------------------------------------------------------------------------------
//     const getZoneStyle = (zoneName) => {
//         const n = (zoneName || '').toLowerCase();
//         if (n.includes('azul')) return { colorClass: 'blue', hex: '#004d98' };
//         if (n.includes('oro')) return { colorClass: 'gold', hex: '#ffd700' };
//         if (n.includes('canels')) return { colorClass: 'blue', hex: '#0055a5' };
//         if (n.includes('tecate')) return { colorClass: 'red', hex: '#cb0909' };
//         if (n.includes('mobil')) return { colorClass: 'red', hex: '#ee1c25' };
//         if (n.includes('red cola')) return { colorClass: 'red', hex: '#da291c' };
//         if (n.includes('moctezuma')) return { colorClass: 'green', hex: '#005826' };
//         if (n.includes('nissan')) return { colorClass: 'gray', hex: '#666666' };
//         return { colorClass: 'red', hex: '#dc3545' };
//     };

//     // --------------------------------------------------------------------------------
//     // RENDERIZADO BARRAS
//     // --------------------------------------------------------------------------------
//     const renderVersusMode = () => {
//         if (zones.length < 2) return null;
//         const zoneA = zones[0];
//         const zoneB = zones[1];
//         const styleA = getZoneStyle(zoneA.zone);
//         const styleB = getZoneStyle(zoneB.zone);

//         return (
//             <div className="noisemeter-screen__versus">
//                 <div className="noisemeter-screen__bar-container noisemeter-screen__bar-container--left">
//                     {/* <div className="noisemeter-screen__label">{zoneA.zone}</div> */}
//                     <div className="noisemeter-screen__bar-track">
//                         <div className={`noisemeter-screen__bar-fill noisemeter-screen__bar-fill--${styleA.colorClass}`} style={{ height: `${zoneA.progressPercent}%`, background: styleA.hex }}>
//                             <span className="noisemeter-screen__score">{Math.floor(zoneA.progressPercent)}%</span>
//                         </div>
//                     </div>
//                 </div>
//                 <div className="noisemeter-screen__vs-badge">VS</div>
//                 <div className="noisemeter-screen__bar-container noisemeter-screen__bar-container--right">
//                     {/* <div className="noisemeter-screen__label">{zoneB.zone}</div> */}
//                     <div className="noisemeter-screen__bar-track">
//                         <div className={`noisemeter-screen__bar-fill noisemeter-screen__bar-fill--${styleB.colorClass}`} style={{ height: `${zoneB.progressPercent}%`, background: styleB.hex }}>
//                             <span className="noisemeter-screen__score">{Math.floor(zoneB.progressPercent)}%</span>
//                         </div>
//                     </div>
//                 </div>
//             </div>
//         );
//     };

//     const renderMultiMode = () => {
//         return (
//             <div className="noisemeter-screen__multi">
//                 {zones.map((z) => {
//                     const style = getZoneStyle(z.zone);
//                     const labelFontSize = zones.length > 5 ? '1.2rem' : '1.8rem';
//                     const labelText = z.zone.replace(/Zona\s/i, ''); 
//                     return (
//                         <div key={z.zone} className="noisemeter-screen__multi-column">
//                             <div className="noisemeter-screen__multi-track">
//                                 <div className="noisemeter-screen__multi-fill" style={{ height: `${z.progressPercent}%`, background: style.hex, boxShadow: `0 0 20px ${style.hex}60` }}></div>
//                             </div>
//                             <p className="noisemeter-screen__label" style={{ fontSize: labelFontSize }}>{labelText}</p>
//                         </div>
//                     );
//                 })}
//             </div>
//         );
//     };

//     // --------------------------------------------------------------------------------
//     // RENDER PRINCIPAL
//     // --------------------------------------------------------------------------------
//     return (
//         <div className="noisemeter-screen">
//             <div className="noisemeter-screen__bg">
//                 {isActiveWomensMode ? (
//                     <img src='/img/selfiefan/Fondo-SelfieFan-Femenil.png' className='noisemeter-screen__bg-img' alt="Fondo Femenil" />
//                 ) : (
//                     <img src='/img/page/backgrounds/background.png' className='noisemeter-screen__bg-img' alt="Fondo Varonil" />
//                 )}
//             </div>

//             <div className='Noisemeter__Screen__Box'>
//                 {/* HEADER CON TEMPORIZADOR */}
//                 <div className="noisemeter-screen__header">
//                     {/* <p className="noisemeter-screen__title">RUIDÓMETRO</p> */}
//                     <img src="/img/page/adsl-five.png" className="Noisemeter__Screen__Logo" alt="Logo" />
//                     {/* <p className="Noisemeter__Screen__Text--Title" style={{ fontFamily: '"GFS Didot", serif' }}>RUIDÓMETRO</p> */}
//                     {/* Mostramos el timer solo si estamos jugando y es mayor a 0 */}
//                     {gameState === 'Running' && timeLeft > 0 && (
//                         // <div className="noisemeter-screen__timer">
//                         <div className="Noisemeter__Screen__Timer">
//                             <i className="bi bi-hourglass-split"></i> {timeLeft} s
//                         </div>
//                     )}
//                 </div>

//                 <div className="noisemeter-screen__content">
//                     {gameState === 'Waiting' && (
//                         <p className="Noisemeter__Screen__Text--Title m-auto">Preparando dinámica...</p>

//                         // <div className="noisemeter-screen__waiting">
//                         //     <p className="noisemeter-screen__pulse-text">PREPARANDO DINÁMICA...</p>
//                         //     {/* <p className="noisemeter-screen__subtext">Escanea el código QR para participar</p> /}
//                         // </div>
//                     )}

//                     {(gameState === 'Running' || gameState === 'Finished') && (
//                         <>
//                             {zones.length === 2 ? renderVersusMode() : renderMultiMode()}
//                         </>
//                     )}

//                     {/* GANADOR - ESTILO RESULTS.JS 
//                         Reutilizamos las clases Results__FullScreenOverlay... de tu CSS global 
//                         o definimos las equivalentes en el SASS local.
//                     */}
//                     {gameState === 'Finished' && winner && (
//                         <div className="Results__FullScreenOverlay">
//                             <Confetti width={width} height={height} recycle={false} numberOfPieces={500} />
                            
//                             <motion.div initial={{ scale: 0 }} animate={{ scale: 1.12 }} transition={{ duration: 0.6, type: 'spring' }} style={{textAlign: 'center'}}>
//                                 <p><i className="bi bi-trophy-fill" style={{fontSize: '5rem', color: '#ffc107'}}></i></p>
                                
//                                 {/* Lógica de visualización de texto */}
//                                 {(() => {
//                                     if (winner === 'tie') {
//                                         return <p className="Results__FullScreenOverlay__OneWinner">¡Ha habido un empate!</p>;
//                                     }
//                                     return (
//                                         <>
//                                             <p className="Results__FullScreenOverlay__Title" style={{fontSize: '3rem'}}>El ganador es...</p>
//                                             <p className="Results__FullScreenOverlay__OneWinner">
//                                                 {winner.toUpperCase()}
//                                             </p>
//                                         </>
//                                     );
//                                 })()}
//                             </motion.div>
//                         </div>
//                     )}
//                 </div>
//             </div>

//             <footer className='SelfieFan__Screen__QR__Footer FooterCredits'>Hecho con ♥ por Interaktia | 2026</footer>
//         </div>
//     );
// };

// export { Noisemeter };

































// /**
//  * Componente: Noisemeter (Screen / Overlay)
//  * Descripción: Muestra la competencia de ruido en la pantalla gigante.
//  * * Modos de visualización automática:
//  * 1. VERSUS (2 zonas): Ideal para Azul vs Oro (Femenil). Muestra barras enfrentadas.
//  * 2. MULTI (N zonas): Ideal para Marcas (Varonil). Muestra columnas verticales lado a lado.
//  * * @param {object} data - Datos en tiempo real recibidos por WebSocket (para reacción inmediata).
//  * @param {boolean} isActiveWomensMode - Bandera para definir el fondo (Varonil/Femenil).
//  */
// const Noisemeter = ({ data, isActiveWomensMode }) => { 
    
//     // --- ESTADOS LOCALES ---
//     const [gameState, setGameState] = useState('Waiting'); // 'Waiting' | 'Running' | 'Finished'
//     const [zones, setZones] = useState([]);                // Lista de zonas y sus progresos
//     const [winner, setWinner] = useState(null);            // Nombre del ganador al finalizar
    
//     // Referencia para el intervalo de actualización (Polling)
//     const pollingRef = useRef(null);

//     // --------------------------------------------------------------------------------
//     // 1. EFECTO: REACCIÓN INMEDIATA (WebSockets)
//     // Escucha cambios en las props 'data' para reaccionar al instante a eventos críticos
//     // como el "Inicio" o el "Fin" del juego, sin esperar al siguiente ciclo de polling.
//     // --------------------------------------------------------------------------------
//     useEffect(() => {
//         if (data) {
//             // Si recibimos un matchId nuevo y no hay ganador, asumimos que el juego arranca/prepara
//             if (data.matchId && !data.winnerZone) {
//                 setGameState('Running');
//             }

//             // Si recibimos la señal de finalización con un ganador
//             if (data.winnerZone) {
//                 setGameState('Finished');
//                 setWinner(data.winnerZone);
                
//                 // Si el socket trae la data final de las zonas, actualizamos para asegurar precisión
//                 if (data.zones) setZones(data.zones);
//             }
//         }
//     }, [data]);

//     // --------------------------------------------------------------------------------
//     // 2. EFECTO: SINCRONIZACIÓN CONTINUA (Polling)
//     // Consulta la API cada 500ms. Esto asegura que las barras suban fluidamente
//     // y corrige cualquier paquete de WebSocket perdido.
//     // --------------------------------------------------------------------------------
//     useEffect(() => {
//         const updateStateFromApi = async () => {
//             const response = await BLGetScreenState();
            
//             if (response.success && response.data) {
//                 const apiData = response.data;
                
//                 // Mapeo de estados numéricos del backend a strings legibles
//                 // 0=Waiting, 1=Running, 2=Finished
//                 let statusStr = 'Waiting';
//                 if (apiData.status === 1 || apiData.status === 'Running') statusStr = 'Running';
//                 if (apiData.status === 2 || apiData.status === 'Finished') statusStr = 'Finished';

//                 // Evitamos regresar a 'Running' si ya estamos en 'Finished' localmente (para evitar parpadeos)
//                 setGameState(prev => (prev === 'Finished' ? 'Finished' : statusStr));
                
//                 // Actualizamos las barras de progreso
//                 setZones(apiData.zones || []);
                
//                 // Si la API confirma que terminó, mostramos ganador
//                 if (statusStr === 'Finished' || (apiData.winnerZone && apiData.winnerZone !== 'none')) {
//                     setWinner(apiData.winnerZone);
//                     setGameState('Finished');
//                 }
//             }
//         };

//         // Ejecutar inmediatamente y luego iniciar el intervalo
//         updateStateFromApi();
//         pollingRef.current = setInterval(updateStateFromApi, 500); 

//         // Limpieza al desmontar el componente
//         return () => {
//             if (pollingRef.current) clearInterval(pollingRef.current);
//         };
//     }, []);

//     // --------------------------------------------------------------------------------
//     // HELPERS VISUALES
//     // --------------------------------------------------------------------------------

//     /**
//      * Determina el color y estilo de la barra según el nombre de la zona.
//      * Soporta modo Femenil (Azul/Oro) y Varonil (Marcas comerciales).
//      */
//     const getZoneStyle = (zoneName) => {
//         const n = (zoneName || '').toLowerCase();
        
//         // --- COLORES ESPECÍFICOS ---
//         if (n.includes('azul')) return { colorClass: 'blue', hex: '#004d98' };
//         if (n.includes('oro')) return { colorClass: 'gold', hex: '#ffd700' };
//         if (n.includes('canels')) return { colorClass: 'blue', hex: '#0055a5' };
//         if (n.includes('tecate')) return { colorClass: 'red', hex: '#cb0909' };
//         if (n.includes('mobil')) return { colorClass: 'red', hex: '#ee1c25' };
//         if (n.includes('red cola')) return { colorClass: 'red', hex: '#da291c' };
//         if (n.includes('moctezuma')) return { colorClass: 'green', hex: '#005826' };
//         if (n.includes('nissan')) return { colorClass: 'gray', hex: '#666666' };
        
//         // Default (Rojo genérico)
//         return { colorClass: 'red', hex: '#dc3545' };
//     };

//     // --------------------------------------------------------------------------------
//     // RENDERIZADO: MODO VERSUS (2 Zonas)
//     // Diseño de enfrentamiento izquierda vs derecha con "VS" en medio.
//     // --------------------------------------------------------------------------------
//     const renderVersusMode = () => {
//         if (zones.length < 2) return null;
//         const zoneA = zones[0];
//         const zoneB = zones[1];
        
//         const styleA = getZoneStyle(zoneA.zone);
//         const styleB = getZoneStyle(zoneB.zone);

//         return (
//             <div className="noisemeter-screen__versus">
//                 {/* --- ZONA IZQUIERDA --- */}
//                 <div className="noisemeter-screen__bar-container noisemeter-screen__bar-container--left">
//                     <div className="noisemeter-screen__label">{zoneA.zone}</div>
//                     <div className="noisemeter-screen__bar-track">
//                         <div className={`noisemeter-screen__bar-fill noisemeter-screen__bar-fill--${styleA.colorClass}`} style={{ height: `${zoneA.progressPercent}%`, background: styleA.hex }}>
//                             <span className="noisemeter-screen__score">{Math.floor(zoneA.progressPercent)}%</span>
//                         </div>
//                     </div>
//                 </div>

//                 {/* --- VS BADGE --- */}
//                 <div className="noisemeter-screen__vs-badge">VS</div>

//                 {/* --- ZONA DERECHA --- */}
//                 <div className="noisemeter-screen__bar-container noisemeter-screen__bar-container--right">
//                     <div className="noisemeter-screen__label">{zoneB.zone}</div>
//                     <div className="noisemeter-screen__bar-track">
//                         <div className={`noisemeter-screen__bar-fill noisemeter-screen__bar-fill--${styleB.colorClass}`} style={{ height: `${zoneB.progressPercent}%`, background: styleB.hex }}>
//                             <span className="noisemeter-screen__score">{Math.floor(zoneB.progressPercent)}%</span>
//                         </div>
//                     </div>
//                 </div>
//             </div>
//         );
//     };

//     // --------------------------------------------------------------------------------
//     // RENDERIZADO: MODO MULTI (N Zonas)
//     // Diseño de columnas verticales distribuidas equitativamente. Ideal para Marcas.
//     // --------------------------------------------------------------------------------
//     const renderMultiMode = () => {
//         return (
//             <div className="noisemeter-screen__multi">
//                 {zones.map((z) => {
//                     const style = getZoneStyle(z.zone);
//                     // Si son muchas zonas (>5), reducimos el tamaño del texto
//                     const labelFontSize = zones.length > 5 ? '1.2rem' : '1.8rem';
//                     // Quitamos la palabra "Zona" para ahorrar espacio visual
//                     const labelText = z.zone.replace(/Zona\s/i, ''); 

//                     return (
//                         <div key={z.zone} className="noisemeter-screen__multi-column">
//                             <div className="noisemeter-screen__multi-track">
//                                 <div className="noisemeter-screen__multi-fill" style={{ height: `${z.progressPercent}%`, background: style.hex, boxShadow: `0 0 20px ${style.hex}60` }}></div>
//                             </div>
//                             <p className="noisemeter-screen__label" style={{ fontSize: labelFontSize }}>{labelText}</p>
//                         </div>
//                     );
//                 })}
//             </div>
//         );
//     };

//     // --------------------------------------------------------------------------------
//     // RENDER PRINCIPAL
//     // --------------------------------------------------------------------------------
//     return (
//         <div className="noisemeter-screen">
            
//             {/* FONDO DINÁMICO (Varonil vs Femenil) */}
//             <div className="noisemeter-screen__bg">
//                 {isActiveWomensMode ? (
//                     <img src='/img/selfiefan/Fondo-SelfieFan-Femenil.png' className='noisemeter-screen__bg-img' alt="Fondo Femenil" />
//                 ) : (
//                     <img src='/img/page/backgrounds/background.png' className='noisemeter-screen__bg-img' alt="Fondo Varonil" />
//                 )}
//             </div>

//             {/* TÍTULO */}
//             <h1 className="noisemeter-screen__title">RUIDÓMETRO</h1>

//             <div className="noisemeter-screen__content">
                
//                 {/* 1. ESPERA: Instrucciones + QR */}
//                 {gameState === 'Waiting' && (
//                     <div className="noisemeter-screen__waiting">
//                         <p className="noisemeter-screen__pulse-text">PREPARANDO DINÁMICA...</p>
//                         <p className="noisemeter-screen__subtext">Escanea el código QR para participar</p>
//                     </div>
//                 )}

//                 {/* 2. JUEGO: Visualización de Barras */}
//                 {(gameState === 'Running' || gameState === 'Finished') && (
//                     <>
//                         {/* Lógica de Renderizado Automático: 2 Zonas = VS | +2 Zonas = Multi */}
//                         {zones.length === 2 ? renderVersusMode() : renderMultiMode()}
//                     </>
//                 )}

//                 {/* 3. FINAL: Overlay de Ganador */}
//                 {gameState === 'Finished' && winner && (
//                     <div className="noisemeter-screen__winner-overlay">
//                         <div className="noisemeter-screen__winner-card">
//                             <p className="noisemeter-screen__winner-label">¡GANADOR!</p>
//                             <h1 className="noisemeter-screen__winner-name">
//                                 {winner === 'tie' ? '¡EMPATE!' : winner.toUpperCase()}
//                             </h1>
//                         </div>
//                     </div>
//                 )}
//             </div>
//         </div>
//     );
// };

// export { Noisemeter };



























// const Noisemeter = ({ data, isActiveWomensMode }) => { // Recibimos props del padre    
//     // --- ESTADOS ---
//     const [gameState, setGameState] = useState('Waiting'); // Waiting | Running | Finished
//     const [zones, setZones] = useState([]);
//     const [winner, setWinner] = useState(null);
//     const [activeZoneConfig, setActiveZoneConfig] = useState('all');

//     // Ref para el polling
//     const pollingRef = useRef(null);

//     // --- EFECTO 1: REACCIÓN INMEDIATA A WEBSOCKETS (Props) ---
//     // Esto hace que la pantalla sea "snappy" (rápida) al recibir comandos
//     useEffect(() => {
//         if (data) {
//             // Si llega mensaje de PREPARE
//             if (data.matchId && !data.winnerZone) {
//                 // Si el juego apenas va a empezar (tiene matchId pero no winner),
//                 // podemos ponerlo en Waiting o Running dependiendo del tiempo
//                 setGameState('Running'); // O 'Waiting' si quieres esperar al polling
//             }

//             // Si llega mensaje de FINISHED
//             if (data.winnerZone) {
//                 setGameState('Finished');
//                 setWinner(data.winnerZone);
//                 // Si vienen zonas en el payload del WS, las actualizamos directo
//                 if (data.zones) setZones(data.zones);
//             }
//         }
//     }, [data]);

//     // --- EFECTO 2: POLLING (Sincronización Continua) ---
//     useEffect(() => {
//         const update = async () => {
//             const response = await BLGetScreenState();
//             if (response.success && response.data) {
//                 const apiData = response.data;
                
//                 // Mapeo de estados del servidor
//                 let statusStr = 'Waiting';
//                 if (apiData.status === 1 || apiData.status === 'Running') statusStr = 'Running';
//                 if (apiData.status === 2 || apiData.status === 'Finished') statusStr = 'Finished';

//                 // Solo actualizamos si no hemos terminado ya (para evitar parpadeos finales)
//                 setGameState(prev => (prev === 'Finished' ? 'Finished' : statusStr));
                
//                 setZones(apiData.zones || []);
//                 setActiveZoneConfig(apiData.activeZone || 'all');
                
//                 if (statusStr === 'Finished' || apiData.winnerZone !== 'none') {
//                     setWinner(apiData.winnerZone);
//                     setGameState('Finished');
//                 }
//             }
//         };

//         // Polling cada 500ms para fluidez
//         update(); // Primer fetch
//         pollingRef.current = setInterval(update, 500);

//         return () => {
//             if (pollingRef.current) clearInterval(pollingRef.current);
//         };
//     }, []);

//     // --- RENDERIZADO DE BARRAS ---
    
//     const renderVersusMode = () => {
//         // Protección: asegurar que hay 2 zonas
//         if (zones.length < 2) return null;

//         const zoneA = zones[0];
//         const zoneB = zones[1];

//         // Lógica de colores basada en nombres o posición
//         const getColor = (name) => {
//             const n = (name || '').toLowerCase();
//             if (n.includes('azul')) return 'blue';
//             if (n.includes('oro')) return 'gold';
//             return 'red'; // Default
//         };

//         return (
//             <div className="noisemeter-screen__versus">
//                 {/* ZONA A */}
//                 <div className={`noisemeter-screen__bar-container noisemeter-screen__bar-container--left`}>
//                     <div className="noisemeter-screen__label">{zoneA.zone}</div>
//                     <div className="noisemeter-screen__bar-track">
//                         <div 
//                             className={`noisemeter-screen__bar-fill noisemeter-screen__bar-fill--${getColor(zoneA.zone)}`}
//                             style={{ height: `${zoneA.progressPercent}%` }}
//                         >
//                             <span className="noisemeter-screen__score">{Math.floor(zoneA.progressPercent)}%</span>
//                         </div>
//                     </div>
//                 </div>

//                 {/* VS Badge */}
//                 <div className="noisemeter-screen__vs-badge">VS</div>

//                 {/* ZONA B */}
//                 <div className={`noisemeter-screen__bar-container noisemeter-screen__bar-container--right`}>
//                     <div className="noisemeter-screen__label">{zoneB.zone}</div>
//                     <div className="noisemeter-screen__bar-track">
//                         <div 
//                             className={`noisemeter-screen__bar-fill noisemeter-screen__bar-fill--${getColor(zoneB.zone)}`}
//                             style={{ height: `${zoneB.progressPercent}%` }}
//                         >
//                             <span className="noisemeter-screen__score">{Math.floor(zoneB.progressPercent)}%</span>
//                         </div>
//                     </div>
//                 </div>
//             </div>
//         );
//     };

//     const renderMultiMode = () => {
//         return (
//             <div className="noisemeter-screen__multi">
//                 {zones.map((z) => (
//                     <div key={z.zone} className="noisemeter-screen__multi-column">
//                         <div className="noisemeter-screen__multi-track">
//                             <div 
//                                 className="noisemeter-screen__multi-fill"
//                                 style={{ height: `${z.progressPercent}%` }}
//                             ></div>
//                         </div>
//                         <p className="noisemeter-screen__label">{z.zone}</p>
//                     </div>
//                 ))}
//             </div>
//         );
//     };

//     return (
//         <div className="noisemeter-screen">
            
//             {/* ─── FONDO DINÁMICO (Varonil vs Femenil) ─── */}
//             <div className="noisemeter-screen__bg">
//                 {isActiveWomensMode ? (
//                     <img 
//                         src='/img/selfiefan/Fondo-SelfieFan-Femenil.png' // Asegúrate de tener un fondo adecuado
//                         className='noisemeter-screen__bg-img' 
//                         alt="Fondo Femenil"
//                     />
//                 ) : (
//                     <img 
//                         src='/img/page/backgrounds/background.png' 
//                         className='noisemeter-screen__bg-img' 
//                         alt="Fondo Varonil"
//                     />
//                 )}
//             </div>

//             {/* HEADER */}
//             <h1 className="noisemeter-screen__title">RUIDÓMETRO</h1>

//             {/* CONTENIDO PRINCIPAL */}
//             <div className="noisemeter-screen__content">
                
//                 {/* 1. ESTADO ESPERA */}
//                 {gameState === 'Waiting' && (
//                     <div className="noisemeter-screen__waiting">
//                         <p className="noisemeter-screen__pulse-text">PREPARANDO DINÁMICA...</p>
//                         <p className="noisemeter-screen__subtext">Escanea el QR para participar</p>
//                     </div>
//                 )}

//                 {/* 2. JUEGO (Barras) */}
//                 {(gameState === 'Running' || gameState === 'Finished') && (
//                     <>
//                         {/* Lógica inteligente de renderizado:
//                            - Si es modo Femenil (generalmente 2 zonas: Azul vs Oro) -> Modo VS
//                            - Si es Varonil (muchas zonas) o solo hay 1 zona activa -> Modo Multi 
//                         */}
//                         {zones.length === 2 ? renderVersusMode() : renderMultiMode()}
//                     </>
//                 )}

//                 {/* 3. GANADOR */}
//                 {gameState === 'Finished' && winner && (
//                     <div className="noisemeter-screen__winner-overlay">
//                         <div className="noisemeter-screen__winner-card">
//                             <p style={{fontSize: '2rem', marginBottom: '1rem'}}>¡GANADOR!</p>
//                             <h1 style={{fontSize: '5rem', margin: 0, color: '#004d98'}}>
//                                 {winner === 'tie' ? '¡EMPATE!' : winner.toUpperCase()}
//                             </h1>
//                         </div>
//                     </div>
//                 )}
//             </div>
//         </div>
//     );
// };

// export { Noisemeter };

























// const Noisemeter = () => {    
//     // --- ESTADOS ---
//     const [gameState, setGameState] = useState('Waiting'); // Waiting | Running | Finished
//     const [zones, setZones] = useState([]);
//     const [winner, setWinner] = useState(null);
//     const [activeZoneConfig, setActiveZoneConfig] = useState('all');

//     // Ref para el polling
//     const pollingRef = useRef(null);

//     // --- CICLO DE VIDA ---
//     useEffect(() => {
//         // Función de actualización
//         const update = async () => {
//             const response = await BLGetScreenState();
//             if (response.success && response.data) {
//                 const data = response.data;
                
//                 // Mapeo de estados del servidor a strings locales
//                 // (Asumiendo que el server devuelve 0,1,2 o strings)
//                 let statusStr = 'Waiting';
//                 if (data.status === 1 || data.status === 'Running') statusStr = 'Running';
//                 if (data.status === 2 || data.status === 'Finished') statusStr = 'Finished';

//                 setGameState(statusStr);
//                 setZones(data.zones || []);
//                 setActiveZoneConfig(data.activeZone || 'all');
                
//                 if (statusStr === 'Finished') {
//                     setWinner(data.winnerZone);
//                 } else {
//                     setWinner(null);
//                 }
//             }
//         };

//         // Iniciar polling rápido (500ms para mayor fluidez visual)
//         update();
//         pollingRef.current = setInterval(update, 500);

//         return () => {
//             if (pollingRef.current) clearInterval(pollingRef.current);
//         };
//     }, []);

//     // --- RENDERIZADO DE BARRAS ---
    
//     // Renderizado específico para 2 zonas (Estilo VS)
//     const renderVersusMode = () => {
//         const zoneA = zones[0];
//         const zoneB = zones[1];

//         // Colores dinámicos según el nombre (puedes ajustar esto a tu paleta real)
//         const getColor = (name) => {
//             const n = name.toLowerCase();
//             if (n.includes('azul') || n.includes('canels')) return 'blue';
//             if (n.includes('oro') || n.includes('mobil')) return 'gold';
//             return 'red'; // Default
//         };

//         return (
//             <div className="noisemeter-screen__versus">
//                 {/* ZONA IZQUIERDA */}
//                 <div className={`noisemeter-screen__bar-container noisemeter-screen__bar-container--left`}>
//                     <div className="noisemeter-screen__label">{zoneA.zone}</div>
//                     <div className="noisemeter-screen__bar-track">
//                         <div 
//                             className={`noisemeter-screen__bar-fill noisemeter-screen__bar-fill--${getColor(zoneA.zone)}`}
//                             style={{ height: `${zoneA.progressPercent}%` }}
//                         >
//                             <span className="noisemeter-screen__score">{Math.floor(zoneA.progressPercent)}%</span>
//                         </div>
//                     </div>
//                 </div>

//                 {/* CENTRO (VS) */}
//                 <div className="noisemeter-screen__vs-badge">VS</div>

//                 {/* ZONA DERECHA */}
//                 <div className={`noisemeter-screen__bar-container noisemeter-screen__bar-container--right`}>
//                     <div className="noisemeter-screen__label">{zoneB.zone}</div>
//                     <div className="noisemeter-screen__bar-track">
//                         <div 
//                             className={`noisemeter-screen__bar-fill noisemeter-screen__bar-fill--${getColor(zoneB.zone)}`}
//                             style={{ height: `${zoneB.progressPercent}%` }}
//                         >
//                             <span className="noisemeter-screen__score">{Math.floor(zoneB.progressPercent)}%</span>
//                         </div>
//                     </div>
//                 </div>
//             </div>
//         );
//     };

//     // Renderizado para N zonas (Barras verticales simples)
//     const renderMultiMode = () => {
//         return (
//             <div className="noisemeter-screen__multi">
//                 {zones.map((z) => (
//                     <div key={z.zone} className="noisemeter-screen__multi-column">
//                         <div className="noisemeter-screen__multi-track">
//                             <div 
//                                 className="noisemeter-screen__multi-fill"
//                                 style={{ height: `${z.progressPercent}%` }}
//                             ></div>
//                         </div>
//                         <p className="noisemeter-screen__label">{z.zone}</p>
//                     </div>
//                 ))}
//             </div>
//         );
//     };

//     return (
//         <div className="noisemeter-screen">
//             {/* FONDO (Puedes poner una imagen de estadio aquí) */}
//             <div className="noisemeter-screen__bg"></div>

//             {/* HEADER */}
//             <h1 className="noisemeter-screen__title">RUIDÓMETRO</h1>

//             {/* CONTENIDO PRINCIPAL */}
//             <div className="noisemeter-screen__content">
                
//                 {gameState === 'Waiting' && (
//                     <div className="noisemeter-screen__waiting">
//                         <p className="noisemeter-screen__pulse-text">PREPARANDO DINÁMICA...</p>
//                         <p className="noisemeter-screen__subtext">Escanea el QR para participar</p>
//                     </div>
//                 )}

//                 {(gameState === 'Running' || gameState === 'Finished') && (
//                     <>
//                         {/* Si son exactamente 2 zonas, usamos modo VS, si no, modo Multi */}
//                         {zones.length === 2 ? renderVersusMode() : renderMultiMode()}
//                     </>
//                 )}

//                 {/* OVERLAY DE GANADOR */}
//                 {gameState === 'Finished' && winner && (
//                     <div className="noisemeter-screen__winner-overlay">
//                         <div className="noisemeter-screen__winner-card">
//                             <p>¡GANADOR!</p>
//                             <h1>{winner === 'tie' ? 'EMPATE' : winner.toUpperCase()}</h1>
//                         </div>
//                     </div>
//                 )}
//             </div>
//         </div>
//     );
// };

// export { Noisemeter };









// import React, { useState, useEffect } from 'react'

// // Importa las funciones de tu BL (ajusta la ruta según tu estructura)
// import { ConnectWebSocketForOverlay, SetSocketMessageHandler, ClearSocketMessageHandler, DisconnectWebSocket } from './../../utilities/BL/Zones'

// // Importa el componente visual hijo (el que tiene los estilos y animación)
// import { Noisemeter as NoisemeterOverlay } from './Overlays/Noisemeter'


// /**
//  * COMPONENTE: NOISEMETER OVERLAY (PARENT CONTAINER)
//  * ------------------------------------------------------------------
//  * Este componente es el "Cerebro" del Overlay. 
//  * 1. Establece la conexión WebSocket como "overlay:noisemeter".
//  * 2. Escucha los audios entrantes.
//  * 3. Pasa la información al componente visual <NoisemeterOverlay />.
//  * ------------------------------------------------------------------
//  */
// const Noisemeter = () => {
    
//     // --- ESTADOS DE CONEXIÓN Y DATOS ---
//     const [messagesData, setMessagesData] = useState(null); // Payload para el hijo
//     const [connectionStatus, setConnectionStatus] = useState('idle');
    
//     // Opcional: Si necesitas manejar el modo femenil/varonil aquí también
//     // const [isActiveWomensMode, setIsActiveWomensMode] = useState(false); 

//     // -------------------------------------------------------------------------
//     // CICLO DE VIDA: CONEXIÓN Y DESCONEXIÓN
//     // -------------------------------------------------------------------------
//     useEffect(() => {
        
//         // 1. Definir la función de conexión
//         const connect = async () => {
//             // Llamamos a la nueva función del BL especificando 'messages'
//             await ConnectWebSocketForOverlay(
//                 'noisemeter', 
//                 (status) => setConnectionStatus(status),
//                 (msg) => console.log('[Overlay] Toast:', msg) // O tu lógica de toast
//             );

//             // Asignamos el manejador de mensajes
//             SetSocketMessageHandler(handleIncomingMessage);
//         };

//         // 2. Definir el manejador de mensajes (El "Switch")
//         const handleIncomingMessage = (message) => {
//             if (!message || !message.type) return;

//             // Filtramos solo los mensajes relevantes para messages
//             // Nota: En el controller C# definimos el type = "messages"
//             if (message.type.toLowerCase() === 'noisemeter') {
//                 // Pasamos el objeto 'data' completo al hijo
//                 // data contiene: { action: 'show_lyric', text: '...', ... }
//                 setMessagesData(message.data);
//             }
//         };

//         // 3. Ejecutar conexión
//         connect();

//         // 4. Cleanup al desmontar
//         return () => {
//             ClearSocketMessageHandler();
//             DisconnectWebSocket();
//         };
//     }, []);

//     // -------------------------------------------------------------------------
//     // RENDERIZADO
//     // -------------------------------------------------------------------------
//     return (
//         <>
//             {/* Renderizamos el componente visual pasándole la data del socket.
//                 Si tuvieras lógica de Modo Femenil, pásala aquí también.
//             */}
//             <NoisemeterOverlay data={messagesData} /> {/* isActiveWomensMode={isActiveWomensMode} */}

//             {/* Opcional: Indicador de error de conexión (útil para debug en OBS) */}
//             {connectionStatus === 'error' && (
//                 <div style={{ position: 'fixed', bottom: 0, right: 0, background: 'red', color: 'white', padding: 5, fontSize: 10 }}>D</div>
//             )}
//         </>
//     );
// };

// export { Noisemeter };